<template>
  <!-- 토스트 -->
  <Transition name="toast">
    <div class="toast" v-if="toastVisible" :class="toastType">
      <p>{{ toastMsg }}</p>
    </div>
  </Transition>

  <!-- 전체 화면 이펙트 오버레이 -->
  <Teleport to="body">
    <!-- 파티클 컨테이너 -->
    <div class="particles-container" v-if="particles.length > 0">
      <span 
        v-for="p in particles" 
        :key="p.id" 
        class="particle"
        :class="p.type"
        :style="{
          left: p.x + '%',
          top: p.y + '%',
          '--vx': p.vx + 'px',
          '--vy': p.vy + 'px',
          '--rotation': p.rotation + 'deg',
          '--scale': p.scale,
          '--duration': p.duration + 's',
          '--delay': p.delay + 's',
          '--hue': p.hue
        }"
      ></span>
    </div>

    <!-- 이벤트 배너 -->
    <Transition name="event-banner">
      <div class="event-overlay" v-if="eventVisible" :class="currentEvent?.type">
        <div class="event-backdrop"></div>
        <div class="event-banner" :style="{ '--event-color': currentEvent?.color }">
          <div class="event-icon-wrap">
            <span class="event-icon">{{ currentEvent?.icon }}</span>
          </div>
          <div class="event-content">
            <span class="event-name">{{ currentEvent?.name }}</span>
            <span class="event-desc">{{ currentEvent?.desc }}</span>
          </div>
          <div class="event-particles">
            <span v-for="i in 8" :key="i" class="event-particle" :style="{ '--p-i': i }"></span>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 잭팟 오버레이 -->
    <Transition name="jackpot-bg">
      <div class="jackpot-overlay" v-if="jackpotEffect">
        <div class="jackpot-rays"></div>
        <div class="jackpot-sparkles">
          <span v-for="i in 16" :key="i" class="sparkle" :style="{ '--i': i }"></span>
        </div>
        <div class="jackpot-text">
          <span v-for="(char, i) in 'JACKPOT!'" :key="i" :style="{ '--char-i': i }">{{ char }}</span>
        </div>
        <div class="jackpot-coins">
          <span v-for="i in 30" :key="'coin'+i" class="falling-coin" :style="{ '--coin-i': i }"></span>
        </div>
      </div>
    </Transition>

    <!-- 빅윈 오버레이 -->
    <Transition name="bigwin-bg">
      <div class="bigwin-overlay" v-if="bigWinEffect">
        <div class="bigwin-burst"></div>
        <div class="bigwin-text">BIG WIN!</div>
        <div class="bigwin-stars">
          <span v-for="i in 12" :key="i" class="burst-star" :style="{ '--star-i': i }"></span>
        </div>
      </div>
    </Transition>

    <!-- 번개 효과 -->
    <div class="lightning-container" v-if="lightningEffect">
      <div class="lightning" v-for="i in 3" :key="i" :style="{ '--l-i': i }"></div>
    </div>

    <!-- 폭죽 효과 -->
    <div class="fireworks-container" v-if="fireworksEffect">
      <div class="firework" v-for="i in 8" :key="i" :style="{ '--fw-i': i }">
        <span v-for="j in 12" :key="j" class="spark" :style="{ '--spark-i': j }"></span>
      </div>
    </div>
  </Teleport>

  <!-- 주간 결산 모달 -->
  <Transition name="settlement">
    <div class="settlement-overlay" v-if="showSettlement">
      <div class="settlement-modal">
        <div class="settlement-header">
          <span class="trophy">🏆</span>
          <h2>주간 결산</h2>
          <span class="week-label">지난 주 결과</span>
        </div>

        <!-- 타워 차트 -->
        <div class="tower-chart">
          <div 
            v-for="(player, idx) in settlementRanking" 
            :key="player.mb_id"
            class="tower-item"
            :class="{ mine: player.mb_id === myData.mb_id }"
          >
            <div class="tower-crown" v-if="idx < 3">
              {{ idx === 0 ? '👑' : idx === 1 ? '🥈' : '🥉' }}
            </div>
            <div class="tower-bar-wrap">
              <div 
                class="tower-bar" 
                :class="'rank-' + (idx + 1)"
                :style="{ height: towerAnimated ? player.heightPercent + '%' : '0%' }"
              ></div>
              <span class="tower-score">{{ formatNumber(player.score) }}</span>
            </div>
            <img :src="getPlayerPic(player)" class="tower-char" />
            <span class="tower-name">{{ player.mb_id === myData.mb_id ? '★나★' : player.mb_id.slice(0,5) }}</span>
          </div>
        </div>

        <!-- 내 결과 -->
        <div class="my-result" v-if="mySettlementRank">
          🎉 <strong>{{ mySettlementRank }}위</strong> 달성! ({{ formatNumber(myData.score) }}점)
        </div>

        <!-- 정복 알림 -->
        <div class="conquest-alert" v-if="pendingConquest">
          ⭐ {{ CHARACTER_NAMES[myData.type] }} 캐릭터 정복!
        </div>

        <button class="settlement-btn" @click="confirmSettlement" :disabled="settlementProcessing">
          {{ settlementProcessing ? '저장 중...' : '기록 저장 & 새 시즌 시작 🚀' }}
        </button>
      </div>
    </div>
  </Transition>

  <!-- 정복 축하 모달 -->
  <Transition name="conquest">
    <div class="conquest-overlay" v-if="conquestVisible" @click="conquestVisible = false">
      <div class="conquest-modal" @click.stop>
        <div class="conquest-badge">🎊</div>
        <h2>캐릭터 정복!</h2>
        <p>{{ conqueredCharName }} 만렙 달성!</p>
        
        <!-- 획득한 영구 버프 표시 -->
        <div class="conquest-reward" v-if="lastConqueredType">
          <span class="reward-label">영구 버프 획득!</span>
          <div class="reward-detail">
            <span class="reward-icon">{{ CHARACTER_TRAITS[lastConqueredType]?.conquest.icon }}</span>
            <span class="reward-text">{{ CHARACTER_TRAITS[lastConqueredType]?.conquest.desc }}</span>
          </div>
        </div>
        
        <div class="conquest-progress">
          정복: {{ conqueredTypes.length }} / 8
        </div>
        
        <div class="new-char" v-if="newCharacterType">
          <p>새 캐릭터</p>
          <img :src="`/imgs/cow_clicker/ch_LV5${newCharacterType}.png`" />
          <span>{{ CHARACTER_NAMES[newCharacterType] }}</span>
          <div class="new-char-trait">
            <span class="trait-icon">{{ CHARACTER_TRAITS[newCharacterType]?.passive.icon }}</span>
            {{ CHARACTER_TRAITS[newCharacterType]?.passive.desc }}
          </div>
        </div>
        
        <div class="all-clear" v-if="isAllCleared">
          🌟 전설의 정복자 🌟<br>
          모든 캐릭터 정복 완료!<br>
          <small>프레스티지 모드 해금!</small>
        </div>
        
        <button @click="conquestVisible = false">
          {{ isAllCleared ? '프레스티지 시작!' : '새 여정 시작!' }}
        </button>
      </div>
    </div>
  </Transition>

  <!-- 프레스티지 모달 -->
  <Transition name="prestige">
    <div class="prestige-overlay" v-if="showPrestigeModal" @click="showPrestigeModal = false">
      <div class="prestige-modal" @click.stop>
        <div class="prestige-header">
          <span class="prestige-icon">⚜️</span>
          <h2>프레스티지</h2>
          <span class="prestige-level">Lv.{{ myData.prestige_level || 0 }}</span>
        </div>
        
        <div class="prestige-info">
          <p>모든 진행 상황을 초기화하고<br>영구 보너스를 획득합니다!</p>
          
          <div class="prestige-bonuses">
            <div class="bonus-item">
              <span class="bonus-icon">⭐</span>
              <span>기본 점수 x{{ 1 + (myData.prestige_level+1 || 0) * 0.1 }}</span>
            </div>
            <div class="bonus-item">
              <span class="bonus-icon">🎰</span>
              <span>슬롯 배율 +{{ (myData.prestige_level +1 || 0) * 5 }}%</span>
            </div>
            <div class="bonus-item">
              <span class="bonus-icon">🧸</span>
              <span>코인 획득 +{{ (myData.prestige_level+1 || 0) * 10 }}%</span>
            </div>
          </div>
          
          <div class="prestige-requirement" v-if="!canPrestige">
            <span>프레스티지 조건: 8마리 정복</span>
          </div>
        </div>
        
        <div class="prestige-buttons">
          <button class="prestige-btn cancel" @click="showPrestigeModal = false">취소</button>
          <button 
            class="prestige-btn confirm" 
            @click="doPrestige"
            :disabled="!canPrestige || prestigeProcessing"
          >
            {{ prestigeProcessing ? '처리 중...' : '프레스티지!' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <div class="dialog simple-clicker" :class="{ 'screen-shake': screenShake }">
    <span class="cls" @click="closeDialog"></span>
    <div class="paper">
      
      <!-- 탭 메뉴 -->
      <div class="tab-menu">
        <button :class="{ active: currentTab === 'game' }" @click="currentTab = 'game'">
          <span class="tab-icon">🎮</span>
          <span class="tab-label">게임</span>
        </button>
        <button 
          :class="{ active: currentTab === 'slot', locked: !isSlotUnlocked }" 
          @click="handleSlotTabClick"
        >
          <span class="tab-icon">{{ isSlotUnlocked ? '🎰' : '🔒' }}</span>
          <span class="tab-label">슬롯</span>
          <span class="charm-badge" v-if="activeCharmsCount > 0">{{ activeCharmsCount }}</span>
        </button>
        <button :class="{ active: currentTab === 'shop' }" @click="currentTab = 'shop'">
          <span class="tab-icon">🛒</span>
          <span class="tab-label">상점</span>
        </button>
        <button :class="{ active: currentTab === 'goldshop' }" @click="currentTab = 'goldshop'">
          <span class="tab-icon">✨</span>
          <span class="tab-label">황금상점</span>
          <span class="coin-badge" v-if="myData.coins > 0">{{ myData.coins }}</span>
        </button>
        <button :class="{ active: currentTab === 'history' }" @click="currentTab = 'history'">
          <span class="tab-icon">📜</span>
          <span class="tab-label">기록</span>
        </button>
      </div>

      <!-- 게임 탭 -->
      <div class="tab-content game-tab" v-show="currentTab === 'game'">

        <!-- 프레스티지 배지 -->
        <div class="prestige-badge-wrap" v-if="myData.prestige_level > 0" @click="showPrestigeModal = true">
          <span class="prestige-badge-icon">⚜️</span>
          <span class="prestige-badge-level">P{{ myData.prestige_level }}</span>
        </div>

        <!-- 정복 현황 -->
        <div class="conquest-status" v-if="conqueredTypes.length > 0 || isAllCleared">
          <div class="conquest-header">
            <span>🏆 정복 현황</span>
            <span class="conquest-count">{{ conqueredTypes.length }}/8</span>
          </div>
          <div class="conquest-grid">
            <div 
              v-for="t in typeEx" 
              :key="t"
              class="conquest-slot"
              :class="{ 
                conquered: conqueredTypes.includes(t), 
                current: t === myData.type && !conqueredTypes.includes(t)
              }"
              :title="CHARACTER_TRAITS[t]?.passive.desc"
            >
              <img :src="`/imgs/cow_clicker/reel_symbol_${t}.png`" />
              <span class="check" v-if="conqueredTypes.includes(t)">✓</span>
              <span class="current-badge" v-if="t === myData.type && !conqueredTypes.includes(t)">NOW</span>
            </div>
          </div>
          <div class="all-clear-badge" v-if="isAllCleared" @click="showPrestigeModal = true">
            🌟 전설의 정복자 🌟 <small>(탭하여 프레스티지)</small>
          </div>
        </div>

        <!-- 현재 캐릭터 특성 표시 -->
        <div class="current-trait" v-if="currentCharTrait">
          <div class="trait-header">
            <span class="trait-char-icon">{{ CHARACTER_TRAITS[myData.type]?.passive.icon }}</span>
            <span class="trait-char-name">{{ CHARACTER_NAMES[myData.type] }}</span>
          </div>
          <div class="trait-passive">
            <span class="trait-label">패시브</span>
            <span class="trait-desc">{{ currentCharTrait.passive.desc }}</span>
          </div>
          <div class="trait-active" v-if="currentCharTrait.active" @click="useActiveSkill">
            <span class="trait-label">스킬</span>
            <span class="trait-desc">{{ currentCharTrait.active.name }}</span>
            <span class="trait-cooldown" v-if="activeSkillCooldown > 0">{{ activeSkillCooldown }}s</span>
            <span class="trait-ready" v-else>READY</span>
          </div>
        </div>
        
        <div class="playground" :style="{ backgroundImage: 'url(' + currentMap + ')' }">
          
          <!-- 점수 & 코인 표시 -->
          <div class="resource-display">
            <div class="resource-item score">
              <span class="resource-icon">⭐</span>
              <span class="resource-value">{{ formatNumber(myData.score) }}</span>
            </div>
            <div class="resource-item coins">
              <span class="resource-icon">🧸</span>
              <span class="resource-value">{{ myData.coins || 0 }}</span>
            </div>
          </div>

          <!-- 스탯 표시 -->
          <div class="stats-display">
            <span title="클릭당 획득">👆 +{{ formatNumber(clickPower) }}</span>
            <span v-if="autoClickPower > 0" title="초당 자동 획득">🤖 +{{ formatNumber(autoClickPower) }}/s</span>
          </div>

          <!-- 레벨 뱃지 -->
          <div class="level-badge">
            <span class="level-num">Lv.{{ currentLevelIndex + 1 }}</span>
            <span class="level-name">{{ currentLevel.name }}</span>
          </div>

          <!-- 콤보 표시 -->
          <Transition name="combo">
            <div class="combo-display" v-if="combo > 0" :class="comboClass">
              <span class="combo-count">{{ combo }} COMBO</span>
              <span class="combo-multi">x{{ comboMultiplier.toFixed(1) }}</span>
              <div class="combo-bar">
                <div class="combo-fill" :style="{ width: comboPercent + '%' }"></div>
              </div>
            </div>
          </Transition>

          <!-- 활성 스킬 효과 표시 -->
          <div class="active-skill-effect" v-if="activeSkillEffect">
            <span class="skill-icon">{{ activeSkillEffect.icon }}</span>
            <span class="skill-name">{{ activeSkillEffect.name }}</span>
            <span class="skill-timer">{{ activeSkillEffect.remaining }}s</span>
          </div>

          <!-- 황금소 -->
          <Transition name="golden">
            <div 
              v-if="goldenCow.active" 
              class="golden-cow"
              @click.stop="catchGoldenCow"
              :style="{ top: goldenCow.y + '%', left: goldenCow.x + '%' }"
            >
              <img src="/imgs/cow_clicker/golden_cow.png" />
              <span class="golden-timer">{{ goldenCow.timeLeft }}s</span>
              <span class="golden-reward">🧸 +{{ goldenCow.reward }}</span>
            </div>
          </Transition>

          <!-- 메인 캐릭터 -->
          <img
            @click="handleClick"
            class="character"
            :class="{ shake: isShaking, 'skill-active': activeSkillEffect }"
            :src="currentLevel.pic"
          />

          <!-- 플로팅 텍스트 -->
          <span
            v-for="text in floatingTexts"
            :key="text.id"
            class="floating-text"
            :class="{ crit: text.crit, auto: text.auto, coin: text.coin, skill: text.skill }"
            :style="{ top: text.y + 'px', left: text.x + 'px' }"
          >
            {{ text.coin ? '🧸' : '+' }}{{ formatNumber(text.amount) }}
          </span>

          <!-- 레벨 진행바 -->
          <div class="level-progress">
            <div class="progress-header">
              <span>다음: {{ nextLevelName }}</span>
              <span>{{ levelPercent }}%</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: levelPercent + '%' }"></div>
            </div>
          </div>
        </div>

        <!-- 랭킹 -->
        <div class="ranking-section">
          <div class="ranking-header">
            <h3>🏆 랭킹</h3>
            <span class="week-indicator">{{ currentWeekLabel }}</span>
          </div>
          <ul class="ranking-list">
            <li 
              v-for="(player, index) in rankingList" 
              :key="player.mb_id"
              :class="{ mine: player.mb_id === myData.mb_id }"
            >
              <span class="rank-num" :class="getRankClass(index)">{{ index + 1 }}</span>
              <img :src="getPlayerPic(player)" class="rank-pic" />
              <span class="rank-name">{{ player.mb_id }}</span>
              <span class="rank-score">⭐ {{ formatNumber(player.score) }}</span>
              <span class="rank-conquered" v-if="player.conquered_types">
                🏆{{ player.conquered_types.split(',').filter(t => t).length }}
              </span>
              <span class="rank-prestige" v-if="player.prestige_level">
                ⚜️{{ player.prestige_level }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <!-- 슬롯 탭 -->
      <div class="tab-content slot-tab" v-show="currentTab === 'slot'">
        
        <!-- 미해금 상태 -->
        <div class="slot-locked" v-if="!isSlotUnlocked">
          <div class="locked-content">
            <span class="locked-icon">🔒</span>
            <h3>도박장 잠금됨</h3>
            <p>황금상점에서 도박장을 해금하세요!</p>
            <button class="unlock-btn" @click="currentTab = 'goldshop'">
              황금상점 가기 →
            </button>
          </div>
        </div>

        <!-- 해금된 슬롯 게임 -->
        <div class="slot-game" v-else :class="{ 'big-win': bigWinEffect, 'jackpot': jackpotEffect, 'winning': slotWinCells.length > 0 && !slotSpinning }">
          
          <!-- 상단: 잔액 & 배팅한도 -->
          <div class="slot-header">
            <div class="balance-display" :class="{ 'win-pulse': winPulse }">
              <span class="balance-label">보유</span>
              <span class="balance-value">⭐ {{ formatNumber(myData.score) }}</span>
            </div>
            <div class="bet-limit-display">
              <span class="limit-label">한도</span>
              <span class="limit-value">{{ formatNumber(maxBetAmount) }}</span>
              <span class="limit-level">Lv.{{ getGoldLevel('betLimit') }}/5</span>
            </div>
          </div>

          <!-- Charm 상점 버튼 -->
          <div class="charm-shop-toggle" @click="showCharmShop = !showCharmShop">
            <span class="charm-toggle-icon">🔮</span>
            <span class="charm-toggle-text">Charm 상점</span>
            <span class="charm-toggle-count" v-if="activeCharmsCount > 0">{{ activeCharmsCount }}개 활성</span>
            <span class="charm-toggle-arrow">{{ showCharmShop ? '▲' : '▼' }}</span>
          </div>

          <!-- Charm 상점 패널 -->
          <Transition name="charm-panel">
            <div class="charm-shop-panel" v-if="showCharmShop">
              <!-- 현재 활성 참 -->
              <div class="active-charms-section" v-if="activeCharms.length > 0">
                <div class="section-title">활성 Charm</div>
                <div class="active-charms-list">
                  <div 
                    v-for="charm in activeCharms" 
                    :key="charm.id"
                    class="active-charm-item"
                    :class="charm.rarity"
                  >
                    <span class="charm-icon">{{ CHARMS[charm.id]?.icon }}</span>
                    <span class="charm-name">{{ CHARMS[charm.id]?.name }}</span>
                  </div>
                </div>
              </div>

              <!-- 상점 -->
              <div class="charm-shop-section">
                <div class="section-title">
                  <span>랜덤 상점</span>
                  <span class="refresh-timer">{{ charmShopRefreshTime }}</span>
                </div>
                <div class="charm-shop-slots">
                  <div 
                    v-for="(item, idx) in charmShopItems" 
                    :key="idx"
                    class="charm-shop-item"
                    :class="[item.rarity, { sold: item.sold, affordable: myData.coins >= item.price && !item.sold }]"
                    @click="buyCharm(idx)"
                  >
                    <div class="charm-item-icon">{{ CHARMS[item.id]?.icon }}</div>
                    <div class="charm-item-name">{{ CHARMS[item.id]?.name }}</div>
                    <div class="charm-item-desc">{{ CHARMS[item.id]?.desc }}</div>
                    <div class="charm-item-price" v-if="!item.sold">
                      <span class="coin-icon">🧸</span>{{ item.price }}
                    </div>
                    <div class="charm-item-sold" v-else>구매완료</div>
                  </div>
                </div>
                <button class="refresh-shop-btn" @click="refreshCharmShop" :disabled="myData.coins < 3">
                  🔄 새로고침 (🧸 3)
                </button>
              </div>
            </div>
          </Transition>

          <!-- 현재 이벤트 표시 -->
          <Transition name="event-indicator">
            <div class="active-event" v-if="currentEvent && !eventVisible" :class="currentEvent.type">
              <span class="active-event-icon">{{ currentEvent.icon }}</span>
              <span class="active-event-name">{{ currentEvent.name }}</span>
              <span class="active-event-spin" v-if="isDoubleChance">2nd</span>
            </div>
          </Transition>

          <!-- 3x5 슬롯 그리드 -->
          <div class="slot-machine" :class="{ 'machine-glow': slotWinCells.length > 0 && !slotSpinning }">
            <div class="slot-frame">
              <div class="neon-border" :class="{ active: slotWinCells.length > 0 && !slotSpinning }"></div>
              
              <div class="slot-grid">
                <div 
                  v-for="(cell, idx) in slotGrid" 
                  :key="idx" 
                  class="slot-cell"
                  :class="{ 
                    winner: slotWinCells.includes(idx),
                    spinning: slotSpinning && !lockedCells.includes(idx),
                    bonus: slotBonus && slotWinCells.includes(idx),
                    'rainbow-glow': slotResult?.totalWin > slotBet * 10 && slotWinCells.includes(idx),
                    'locked-cell': lockedCells.includes(idx),
                    'wild-cell': wildCells.includes(idx)
                  }"
                  :style="{ '--cell-delay': (idx % 5) * 0.05 + 's' }"
                >
                  <div class="cell-inner">
                    <img 
                      :src="cell === 'WILD' ? '/imgs/cow_clicker/wild_symbol.png' : SYMBOLS[cell]?.img" 
                      class="slot-symbol" 
                    />
                    <div class="locked-indicator" v-if="lockedCells.includes(idx)">
                      <span>🔒</span>
                    </div>
                    <div class="wild-indicator" v-if="wildCells.includes(idx)">
                      <span>🃏</span>
                    </div>
                  </div>
                  <div class="cell-particles" v-if="slotWinCells.includes(idx) && !slotSpinning">
                    <span v-for="i in 6" :key="i" class="cell-spark" :style="{ '--spark-i': i }"></span>
                  </div>
                </div>
              </div>
              
              <!-- 결과 표시 -->
              <Transition name="win-popup">
                <div 
                  class="slot-result" 
                  v-if="slotResult && !slotSpinning && slotResult.totalWin > 0"
                  :class="getWinClass()"
                >
                  <div class="win-display">
                    <div class="win-glow"></div>
                    <span class="win-label">
                      <span v-for="(char, i) in getWinLabel()" :key="i" class="win-char" :style="{ '--char-i': i }">{{ char }}</span>
                    </span>
                    <span class="win-amount">
                      <span class="plus">+</span>{{ formatNumber(slotResult?.totalWin || 0) }}
                    </span>
                    <div class="win-multiplier" v-if="slotResult.totalWin >= slotBet * 5">
                      x{{ (slotResult.totalWin / slotBet).toFixed(1) }}
                    </div>
                    <div class="event-effect-notice" v-if="slotResult.eventEffect">
                      {{ slotResult.eventEffect }}
                    </div>
                    <div class="charm-effect-notice" v-if="slotResult.charmEffect">
                      🔮 {{ slotResult.charmEffect }}
                    </div>
                    <div class="win-details" v-if="slotResult?.matches?.length > 0">
                      <span 
                        v-for="(match, i) in slotResult.matches.slice(0, 3)" 
                        :key="i" 
                        class="win-line"
                        :class="[SYMBOLS[match.symbol]?.tier]"
                      >
                        <img :src="SYMBOLS[match.symbol]?.img" class="win-symbol" />
                        {{ match.pattern.name }} x{{ match.finalMult.toFixed(1) }}
                      </span>
                      <span v-if="slotResult.matches.length > 3" class="win-more">
                        +{{ slotResult.matches.length - 3 }}개 더
                      </span>
                    </div>
                    <div v-if="slotResult?.hasBonus" class="bonus-notice">
                      <span class="bonus-fire">🔥</span>
                      4+ 심볼 패턴 → 배율 2배!
                      <span class="bonus-fire">🔥</span>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>

          <!-- 배팅 컨트롤 -->
          <div class="bet-controls">
            <div class="bet-section">
              <span class="bet-label">배팅 금액</span>
              <div class="bet-input-wrap">
                <button class="bet-btn" @click="adjustBet(-100000)">-10만</button>
                <input 
                  type="number" 
                  v-model.number="slotBet" 
                  class="bet-input"
                  min="1"
                  :max="Math.min(myData.score, maxBetAmount)"
                  @change="clampBet"
                />
                <button class="bet-btn" @click="adjustBet(100000)">+10만</button>
              </div>
              <div class="bet-presets">
                <button @click="setBet(1000000)">100만</button>
                <button @click="setBet(10000000)">1천만</button>
                <button @click="setBet(100000000)">1억</button>
                <button @click="setBet(Math.floor(Math.min(myData.score, maxBetAmount) / 2))">절반</button>
                <button @click="setBet(Math.min(myData.score, maxBetAmount))">MAX</button>
              </div>
              <div class="event-hint" v-if="isHighRiskBet">
                ⚡ 고위험 배팅! 이벤트 발생 가능
              </div>
              <div class="charm-hint" v-if="totalCharmBonus > 0">
                🔮 Charm 보너스: 당첨률 +{{ (totalCharmBonus * 100).toFixed(1) }}%
              </div>
            </div>
            
            <button 
              class="spin-btn"
              @click="spinSlot"
              @mousedown="playButton"
              :disabled="slotSpinning || myData.score < slotBet || slotBet < 1 || eventAnimating"
            >
              <span v-if="slotSpinning" class="spin-anim">🎰</span>
              <span v-else-if="eventAnimating" class="spin-anim">⚡</span>
              <template v-else>
                <span class="spin-text">SPIN!</span>
                <span class="spin-cost">({{ formatNumber(slotBet) }})</span>
              </template>
            </button>
          </div>

          <!-- 배당표 -->
          <div class="paytable">
            <div class="paytable-header" @click="paytableOpen = !paytableOpen">
              <span>📋 배당표</span>
              <span class="toggle-icon">{{ paytableOpen ? '▲' : '▼' }}</span>
            </div>
            
            <div class="paytable-body" v-show="paytableOpen">
              <div class="tier-section">
                <div class="tier-title">심볼 등급</div>
                <div class="tier-grid">
                  <div 
                    v-for="tier in TIER_INFO" 
                    :key="tier.tier" 
                    class="tier-card"
                    :class="tier.tier"
                  >
                    <div class="tier-header">
                      <span class="tier-name">{{ tier.name }}</span>
                      <span class="tier-mult">{{ tier.mult }}</span>
                    </div>
                    <div class="tier-symbols">
                      <img 
                        v-for="sym in symbolsByTier[tier.tier]" 
                        :key="sym.id"
                        :src="sym.img" 
                        class="tier-img"
                        :title="sym.name"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="event-section">
                <div class="event-title">⚡ 고위험 이벤트 (10% 이상 or 1억+ 배팅)</div>
                <div class="event-list">
                  <div class="event-item good">
                    <span>🎯 잭팟찬스</span>
                    <span>풀그리드 5%!</span>
                  </div>
                  <div class="event-item good">
                    <span>🔥 파이어</span>
                    <span>5매칭 10%!</span>
                  </div>
                  <div class="event-item good">
                    <span>⚡ 럭키</span>
                    <span>4매칭 20%!</span>
                  </div>
                </div>
              </div>

              <div class="pattern-section">
                <div class="pattern-title">패턴 배율</div>
                <div class="pattern-grid">
                  <div class="pattern-item" v-for="p in PATTERN_INFO" :key="p.name">
                    <span class="pattern-visual">{{ p.visual }}</span>
                    <span class="pattern-name">{{ p.name }}</span>
                    <span class="pattern-mult" :class="{ bonus: p.is4plus }">x{{ p.mult }}</span>
                  </div>
                </div>
              </div>

              <div class="paytable-note">
                💡 최종배율 = 패턴 × 심볼등급 × 보너스(2x) × Charm
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- 상점 탭 -->
      <div class="tab-content shop-tab" v-show="currentTab === 'shop'">
        <div class="shop-header">
          <h3>🛒 상점</h3>
          <span class="score-badge">⭐ {{ formatNumber(myData.score) }}</span>
        </div>

        <ul class="upgrade-list">
          <li 
            v-for="upgrade in shopUpgrades" 
            :key="upgrade.id"
            :class="{ affordable: canAffordShop(upgrade), maxed: isMaxed(upgrade) }"
          >
            <div class="upgrade-info">
              <span class="upgrade-icon">{{ upgrade.icon }}</span>
              <div class="upgrade-text">
                <h4>{{ upgrade.name }}</h4>
                <p>{{ upgrade.desc }}</p>
                <span class="upgrade-level">Lv.{{ getLevel(upgrade.id) }} / {{ upgrade.maxLevel }}</span>
              </div>
            </div>
            <button 
              @click="buyShopUpgrade(upgrade)"
              :disabled="!canAffordShop(upgrade) || isMaxed(upgrade)"
            >
              {{ isMaxed(upgrade) ? 'MAX' : '⭐ ' + formatNumber(getShopCost(upgrade)) }}
            </button>
          </li>
        </ul>
      </div>

      <!-- 황금상점 탭 -->
      <div class="tab-content shop-tab gold-shop" v-show="currentTab === 'goldshop'">
        <div class="shop-header gold">
          <h3>✨ 황금상점</h3>
          <span class="coin-badge-large">🧸 {{ myData.coins || 0 }}</span>
        </div>

        <p class="gold-shop-desc">황금소를 잡아 코인을 모으세요!</p>

        <ul class="upgrade-list gold">
          <li 
            v-for="upgrade in goldUpgrades" 
            :key="upgrade.id"
            :class="{ 
              affordable: canAffordGold(upgrade), 
              maxed: isGoldMaxed(upgrade),
              featured: upgrade.featured
            }"
          >
            <div class="upgrade-info">
              <span class="upgrade-icon">{{ upgrade.icon }}</span>
              <div class="upgrade-text">
                <h4>{{ upgrade.name }}</h4>
                <p>{{ getGoldUpgradeDesc(upgrade) }}</p>
                <span class="upgrade-level">
                  {{ upgrade.maxLevel === 1 
                    ? (isGoldMaxed(upgrade) ? '해금됨' : '미해금') 
                    : 'Lv.' + getGoldLevel(upgrade.id) + ' / ' + upgrade.maxLevel 
                  }}
                </span>
              </div>
            </div>
            <button 
              @click="buyGoldUpgrade(upgrade)"
              :disabled="!canAffordGold(upgrade) || isGoldMaxed(upgrade)"
            >
              {{ isGoldMaxed(upgrade) ? (upgrade.maxLevel === 1 ? '✓' : 'MAX') : '🧸 ' + getGoldCost(upgrade) }}
            </button>
          </li>
        </ul>
      </div>

      <!-- 기록 탭 -->
      <div class="tab-content history-tab" v-show="currentTab === 'history'">
        <div class="history-header">
          <h3>📜 주간 기록</h3>
        </div>
        
        <div class="history-list" v-if="historyList.length > 0">
          <div 
            v-for="record in historyList" 
            :key="record.id"
            class="history-item"
            :class="{ 'top-rank': record.rank_position <= 3 }"
          >
            <div class="history-week">{{ record.week_year }}</div>
            <div class="history-rank">
              <span v-if="record.rank_position === 1">🥇</span>
              <span v-else-if="record.rank_position === 2">🥈</span>
              <span v-else-if="record.rank_position === 3">🥉</span>
              <span v-else>{{ record.rank_position }}위</span>
            </div>
            <div class="history-score">⭐ {{ formatNumber(record.final_score) }}</div>
            <img 
              :src="`/imgs/cow_clicker/reel_symbol_${record.character_type}.png`"  
              class="history-char"
              :title="CHARACTER_NAMES[record.character_type]"
            />
            <div class="history-conquest" v-if="record.conquered_this_week">⭐정복</div>
          </div>
        </div>
        
        <div class="history-empty" v-else>
          <p>아직 기록이 없습니다</p>
          <p>매주 월요일에 결산됩니다!</p>
        </div>
      </div>

    </div>
  </div>
</template>


<script>
import { ref, inject, onMounted, computed, onUnmounted } from 'vue';
import { useBackButton } from '@/utils/useBackBtn';

export default {
  props: {
    oth_showCowClicker: Function
  },
  setup(props) {
    const sql_query = inject('sql_query');
    const getMbid = inject('getMbid');

    // ==================== 사운드 시스템 ====================
    let audioCtx = null;
    
    function getAudioContext() {
      if (!audioCtx) {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtx.state === 'suspended') audioCtx.resume();
      return audioCtx;
    }

    function playTone(freq, duration, type = 'sine', volume = 0.3) {
      try {
        const ctx = getAudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = type;
        gain.gain.setValueAtTime(volume, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + duration);
      } catch (e) { console.log('Audio error:', e); }
    }

    function playChord(freqs, duration, type = 'sine', volume = 0.2) {
      freqs.forEach(freq => playTone(freq, duration, type, volume / freqs.length));
    }

    function playSpinStart() {
      playTone(300, 0.08, 'square', 0.15);
      setTimeout(() => playTone(400, 0.08, 'square', 0.18), 40);
      setTimeout(() => playTone(500, 0.08, 'square', 0.2), 80);
      setTimeout(() => playTone(600, 0.1, 'square', 0.22), 120);
    }
    function playSpinTick() { playTone(250 + Math.random() * 150, 0.03, 'square', 0.08); }
    function playSpinStop() {
      playTone(200, 0.12, 'triangle', 0.25);
      setTimeout(() => playTone(150, 0.08, 'triangle', 0.15), 50);
    }
    function playWinTiny() {
      playTone(523, 0.15, 'sine', 0.2);
      setTimeout(() => playTone(659, 0.2, 'sine', 0.25), 80);
    }
    function playWinSmall() {
      [523, 659, 784].forEach((freq, i) => {
        setTimeout(() => playTone(freq, 0.2, 'sine', 0.28), i * 80);
      });
    }
    function playWinMedium() {
      [523, 659, 784, 1047].forEach((freq, i) => {
        setTimeout(() => {
          playTone(freq, 0.25, 'sine', 0.32);
          playTone(freq * 0.5, 0.2, 'triangle', 0.1);
        }, i * 100);
      });
      setTimeout(() => playChord([1047, 1319, 1568], 0.4, 'sine', 0.35), 500);
    }
    function playWinBig() {
      for (let i = 0; i < 8; i++) setTimeout(() => playTone(100, 0.05, 'square', 0.2), i * 40);
      [523, 523, 659, 784, 1047, 784, 1047].forEach((freq, i) => {
        setTimeout(() => {
          playTone(freq, 0.2, 'sine', 0.35);
          playTone(freq * 0.5, 0.15, 'triangle', 0.12);
        }, 350 + i * 100);
      });
      setTimeout(() => {
        playChord([1047, 1319, 1568, 2093], 0.6, 'sine', 0.4);
        playTone(262, 0.5, 'triangle', 0.2);
      }, 1100);
    }
    function playWinJackpot() {
      for (let i = 0; i < 12; i++) setTimeout(() => playTone(80 + i * 5, 0.06, 'square', 0.18 + i * 0.01), i * 35);
      for (let i = 0; i < 10; i++) setTimeout(() => playTone(300 + i * 80, 0.1, 'sine', 0.25), 400 + i * 50);
      [784, 784, 1047, 1319, 1047, 1319, 1568].forEach((freq, i) => {
        setTimeout(() => {
          playTone(freq, 0.25, 'sine', 0.4);
          playTone(freq * 0.5, 0.2, 'triangle', 0.15);
          playTone(freq * 2, 0.15, 'sine', 0.1);
        }, 900 + i * 120);
      });
      [1800, 2200, 2600].forEach(delay => {
        setTimeout(() => {
          playChord([1047, 1319, 1568, 2093], 0.5, 'sine', 0.35);
          playTone(262, 0.4, 'triangle', 0.2);
        }, delay);
      });
    }
    function playLose() {
      playTone(250, 0.2, 'sine', 0.15);
      setTimeout(() => playTone(200, 0.25, 'sine', 0.12), 120);
      setTimeout(() => playTone(150, 0.3, 'sine', 0.1), 250);
    }
    function playButton() { playTone(800, 0.04, 'sine', 0.12); }
    function playBonus() {
      [784, 988, 1175, 1318, 1568].forEach((freq, i) => {
        setTimeout(() => {
          playTone(freq, 0.12, 'sine', 0.3);
          playTone(freq * 1.5, 0.1, 'triangle', 0.1);
        }, i * 50);
      });
      setTimeout(() => playChord([1568, 1976, 2349], 0.3, 'sine', 0.25), 300);
    }
    function playCoinDrop(count = 5) {
      for (let i = 0; i < count; i++) {
        setTimeout(() => {
          const baseFreq = 2000 + Math.random() * 1500;
          playTone(baseFreq, 0.1, 'sine', 0.12);
          playTone(baseFreq * 1.5, 0.08, 'triangle', 0.06);
        }, i * 70 + Math.random() * 30);
      }
    }
    function playCoinShower() {
      playCoinDrop(10);
      setTimeout(() => playCoinDrop(8), 350);
      setTimeout(() => {
        playTone(3000, 0.15, 'sine', 0.18);
        playTone(3500, 0.2, 'triangle', 0.12);
      }, 700);
    }
    function playCoinExplosion() {
      playTone(100, 0.15, 'square', 0.3);
      playTone(80, 0.2, 'sawtooth', 0.2);
      for (let i = 0; i < 20; i++) setTimeout(() => playTone(2500 + Math.random() * 2000, 0.08, 'sine', 0.1), 150 + i * 40);
      setTimeout(() => playCoinDrop(12), 500);
      setTimeout(() => playCoinDrop(10), 900);
      setTimeout(() => {
        playTone(3500, 0.25, 'sine', 0.2);
        playTone(4000, 0.3, 'sine', 0.15);
      }, 1300);
    }
    function playChaChing() {
      playTone(2000, 0.12, 'sine', 0.25);
      playTone(2500, 0.15, 'triangle', 0.15);
      setTimeout(() => {
        playTone(2400, 0.1, 'sine', 0.2);
        playTone(3000, 0.12, 'triangle', 0.12);
      }, 100);
    }
    function playLocked() {
      playTone(200, 0.12, 'square', 0.18);
      setTimeout(() => playTone(150, 0.15, 'square', 0.12), 80);
    }
    function playEventGood() {
      [523, 659, 784, 1047].forEach((freq, i) => {
        setTimeout(() => playTone(freq, 0.15, 'sine', 0.25), i * 80);
      });
    }
    function playEventBad() {
      playTone(200, 0.3, 'sawtooth', 0.2);
      setTimeout(() => playTone(150, 0.4, 'sawtooth', 0.15), 150);
    }
    function playSkillActivate() {
      [659, 784, 988, 1175].forEach((freq, i) => {
        setTimeout(() => playTone(freq, 0.1, 'sine', 0.3), i * 60);
      });
    }
    function playCharmBuy() {
      playTone(523, 0.1, 'sine', 0.25);
      setTimeout(() => playTone(784, 0.15, 'sine', 0.3), 100);
      setTimeout(() => playTone(1047, 0.2, 'sine', 0.25), 200);
    }

    // ==================== 파티클 시스템 ====================
    const particles = ref([]);
    const screenShake = ref(false);
    const bigWinEffect = ref(false);
    const jackpotEffect = ref(false);
    const lightningEffect = ref(false);
    const fireworksEffect = ref(false);
    const winPulse = ref(false);

    function spawnParticles(count, type = 'coin', options = {}) {
      const { startX = 50, startY = 50, spread = 40, minDuration = 1.5, maxDuration = 2.5 } = options;
      for (let i = 0; i < count; i++) {
        setTimeout(() => {
          const id = Date.now() + Math.random();
          particles.value.push({
            id, type,
            x: startX + (Math.random() - 0.5) * spread,
            y: startY + (Math.random() - 0.5) * 20,
            vx: (Math.random() - 0.5) * 200,
            vy: -100 - Math.random() * 150,
            rotation: Math.random() * 360,
            scale: 0.5 + Math.random() * 1,
            duration: minDuration + Math.random() * (maxDuration - minDuration),
            delay: i * 0.02,
            hue: Math.floor(Math.random() * 360)
          });
          setTimeout(() => { particles.value = particles.value.filter(p => p.id !== id); }, 3000);
        }, i * 30);
      }
    }
    function triggerScreenShake(intensity = 1, duration = 500) {
      screenShake.value = true;
      document.documentElement.style.setProperty('--shake-intensity', intensity);
      setTimeout(() => { screenShake.value = false; }, duration);
    }
    function triggerLightning() {
      lightningEffect.value = true;
      setTimeout(() => { lightningEffect.value = false; }, 400);
    }
    function triggerFireworks() {
      fireworksEffect.value = true;
      setTimeout(() => { fireworksEffect.value = false; }, 2000);
    }
    function triggerWinPulse() {
      winPulse.value = true;
      setTimeout(() => { winPulse.value = false; }, 1000);
    }
    function triggerSmallWin() {
      triggerWinPulse();
      spawnParticles(5, 'sparkle', { startY: 50, spread: 30 });
    }
    function triggerMediumWin() {
      triggerWinPulse();
      spawnParticles(12, 'coin', { startY: 45 });
      spawnParticles(8, 'sparkle', { startY: 50 });
      triggerScreenShake(0.5, 300);
    }
    function triggerNiceWin() {
      triggerWinPulse();
      spawnParticles(20, 'coin', { startY: 40 });
      spawnParticles(15, 'star', { startY: 45 });
      triggerScreenShake(0.8, 400);
    }
    function triggerBigWin() {
      bigWinEffect.value = true;
      spawnParticles(30, 'coin', { startY: 30 });
      spawnParticles(20, 'star', { startY: 40 });
      spawnParticles(15, 'sparkle', { startY: 20, spread: 80 });
      triggerScreenShake(1.2, 600);
      triggerLightning();
      triggerWinPulse();
      setTimeout(() => { bigWinEffect.value = false; }, 2500);
    }
    function triggerJackpot() {
      jackpotEffect.value = true;
      spawnParticles(50, 'coin', { startY: 20, spread: 100 });
      setTimeout(() => spawnParticles(40, 'star', { startY: 30 }), 200);
      setTimeout(() => spawnParticles(30, 'sparkle', { startY: 40 }), 400);
      triggerScreenShake(2, 1000);
      triggerLightning();
      setTimeout(() => triggerLightning(), 300);
      setTimeout(() => triggerLightning(), 600);
      triggerFireworks();
      triggerWinPulse();
      setTimeout(() => { jackpotEffect.value = false; }, 4000);
    }

    // 기본 상태
    const currentTab = ref('game');
    const isShaking = ref(false);
    const floatingTexts = ref([]);
    const toastVisible = ref(false);
    const toastMsg = ref('');
    const toastType = ref('');
    const paytableOpen = ref(false);

    // 유저 데이터
    const myData = ref({
      mb_id: '', score: 0, coins: 0, type: 'G', upgrades: '{}', gold_upgrades: '{}',
      last_reset_week: '', conquered_types: '', all_clear: 0, prestige_level: 0,
      active_charms: '[]', charm_shop: '[]', charm_refresh_at: ''
    });

    const rankingList = ref([]);
    const typeEx = ['G', 'T', 'S', 'E', 'V', 'L', 'C', 'O'];

    // 캐릭터 이름
    const CHARACTER_NAMES = {
      G: '사자', T: '까마귀', S: '거북이', E: '고양이',
      V: '토끼', L: '강아지', C: '코끼리', O: '미어캣'
    };

    // ==================== 캐릭터 고유 특성 시스템 ====================
    const CHARACTER_TRAITS = {
      G: { // 사자 - 공격형
        passive: { id: 'clickPower', icon: '🦁', value: 0.15, desc: '클릭 파워 +15%' },
        active: { name: '사자의 포효', icon: '🔊', cooldown: 60, duration: 10, effect: 'critBoost', value: 0.5, desc: '10초간 크리 확률 +50%' },
        conquest: { id: 'click', icon: '👊', value: 0.03, desc: '영구 클릭 +3%' }
      },
      T: { // 까마귀 - 자동형
        passive: { id: 'autoBoost', icon: '🐦‍⬛', value: 0.2, desc: '자동 수익 +20%' },
        active: { name: '까마귀 떼', icon: '🌑', cooldown: 90, duration: 15, effect: 'autoMulti', value: 3, desc: '15초간 자동클릭 3배' },
        conquest: { id: 'auto', icon: '🤖', value: 0.05, desc: '영구 자동 +5%' }
      },
      S: { // 거북이 - 방어형
        passive: { id: 'comboKeep', icon: '🐢', value: 0.3, desc: '콤보 유지시간 +30%' },
        active: { name: '거북이 방패', icon: '🛡️', cooldown: 120, duration: 20, effect: 'comboFreeze', value: 1, desc: '20초간 콤보 안 끊김' },
        conquest: { id: 'combo', icon: '🔥', value: 0.05, desc: '영구 콤보 +5%' }
      },
      E: { // 고양이 - 행운형
        passive: { id: 'critChance', icon: '🐱', value: 0.1, desc: '크리티컬 확률 +10%' },
        active: { name: '고양이 발톱', icon: '🐾', cooldown: 45, duration: 8, effect: 'superCrit', value: 5, desc: '8초간 크리 데미지 5배' },
        conquest: { id: 'crit', icon: '⚡', value: 0.03, desc: '영구 크리 +3%' }
      },
      V: { // 토끼 - 황금형
        passive: { id: 'goldenChance', icon: '🐰', value: 0.05, desc: '황금소 출현 +5%' },
        active: { name: '토끼 행운', icon: '🍀', cooldown: 180, duration: 30, effect: 'goldenMagnet', value: 1, desc: '30초간 황금소 필수 출현' },
        conquest: { id: 'golden', icon: '✨', value: 0.02, desc: '영구 황금 +2%' }
      },
      L: { // 강아지 - 코인형
        passive: { id: 'coinBonus', icon: '🐕', value: 2, desc: '황금소 코인 +2' },
        active: { name: '강아지 탐색', icon: '🔍', cooldown: 60, duration: 0, effect: 'instantGolden', value: 1, desc: '즉시 황금소 소환' },
        conquest: { id: 'coin', icon: '🧸', value: 1, desc: '영구 코인 +1' }
      },
      C: { // 코끼리 - 슬롯형
        passive: { id: 'slotBoost', icon: '🐘', value: 0.03, desc: '슬롯 당첨률 +3%' },
        active: { name: '코끼리 행진', icon: '🎪', cooldown: 120, duration: 5, effect: 'slotGuarantee', value: 0.5, desc: '다음 스핀 당첨 확률 +50%' },
        conquest: { id: 'slot', icon: '🎰', value: 0.02, desc: '영구 슬롯 +2%' }
      },
      O: { // 미어캣 - 만능형
        passive: { id: 'allBoost', icon: '🦡', value: 0.08, desc: '모든 수익 +8%' },
        active: { name: '미어캣 경계', icon: '👀', cooldown: 90, duration: 20, effect: 'allMulti', value: 1.5, desc: '20초간 모든 획득 1.5배' },
        conquest: { id: 'exp', icon: '📈', value: 0.05, desc: '영구 수익 +5%' }
      }
    };

    const currentCharTrait = computed(() => CHARACTER_TRAITS[myData.value.type]);
    const activeSkillCooldown = ref(0);
    const activeSkillEffect = ref(null);
    let skillCooldownInterval = null;
    let skillEffectInterval = null;

    function useActiveSkill() {
      if (activeSkillCooldown.value > 0) return;
      const trait = currentCharTrait.value;
      if (!trait?.active) return;

      playSkillActivate();
      activeSkillCooldown.value = trait.active.cooldown;
      
      // 즉시 효과 (강아지)
      if (trait.active.effect === 'instantGolden') {
        spawnGoldenCow();
        showToast(`${trait.active.name} 발동!`, 'skill');
      } 
      // 지속 효과
      else if (trait.active.duration > 0) {
        activeSkillEffect.value = {
          ...trait.active,
          remaining: trait.active.duration
        };
        showToast(`${trait.active.name} 발동! (${trait.active.duration}초)`, 'skill');
        
        skillEffectInterval = setInterval(() => {
          if (activeSkillEffect.value) {
            activeSkillEffect.value.remaining--;
            if (activeSkillEffect.value.remaining <= 0) {
              activeSkillEffect.value = null;
              clearInterval(skillEffectInterval);
            }
          }
        }, 1000);
      }

      // 쿨다운 시작
      skillCooldownInterval = setInterval(() => {
        activeSkillCooldown.value--;
        if (activeSkillCooldown.value <= 0) {
          clearInterval(skillCooldownInterval);
        }
      }, 1000);
    }

    // ==================== CHARM 시스템 ====================
    const CHARMS = {
      // Common - 기본 슬롯 강화
      luckyClover: { id: 'luckyClover', name: '행운의 클로버', icon: '🍀', rarity: 'common', effect: 'slotWinRate', value: 0.02, desc: '슬롯 당첨률 +2%' },
      silverCoin: { id: 'silverCoin', name: '은빛 동전', icon: '🪙', rarity: 'common', effect: 'slotWinRate', value: 0.03, desc: '슬롯 당첨률 +3%' },
      rabbitFoot: { id: 'rabbitFoot', name: '토끼 발', icon: '🐾', rarity: 'common', effect: 'line3Rate', value: 0.05, desc: '3매칭 확률 +5%' },
      spinTicket: { id: 'spinTicket', name: '스핀 티켓', icon: '🎫', rarity: 'common', effect: 'spinCostReduce', value: 0.1, desc: '스핀 비용 -10%' },
      
      // Rare - 중급 슬롯 강화
      goldenDice: { id: 'goldenDice', name: '황금 주사위', icon: '🎲', rarity: 'rare', effect: 'jackpotRate', value: 0.01, desc: '잭팟 확률 +1%' },
      fireRing: { id: 'fireRing', name: '불의 반지', icon: '💍', rarity: 'rare', effect: 'bonusMult', value: 0.3, desc: '보너스 배율 +30%' },
      starFragment: { id: 'starFragment', name: '별의 조각', icon: '⭐', rarity: 'rare', effect: 'line4Rate', value: 0.1, desc: '4매칭 확률 +10%' },
      luckySeven: { id: 'luckySeven', name: '럭키 세븐', icon: '7️⃣', rarity: 'rare', effect: 'sevenSymbol', value: 0.15, desc: '7 심볼 확률 +15%' },
      
      // Epic - 고급 슬롯 강화
      dragonScale: { id: 'dragonScale', name: '용의 비늘', icon: '🐉', rarity: 'epic', effect: 'line5Rate', value: 0.05, desc: '5매칭 확률 +5%' },
      phoenixFeather: { id: 'phoenixFeather', name: '불사조 깃털', icon: '🔥', rarity: 'epic', effect: 'slotWinRate', value: 0.08, desc: '슬롯 당첨률 +8%' },
      wildCard: { id: 'wildCard', name: '와일드 카드', icon: '🃏', rarity: 'epic', effect: 'wildChance', value: 0.1, desc: '와일드 심볼 +10%' },
      
      // Legendary - 최고급 슬롯 강화
      infinityGem: { id: 'infinityGem', name: '무한의 보석', icon: '💎', rarity: 'legendary', effect: 'allSlotBoost', value: 0.15, desc: '모든 슬롯 보너스 +15%' },
      goldenReels: { id: 'goldenReels', name: '황금 릴', icon: '🌟', rarity: 'legendary', effect: 'slotRewardMult', value: 2, desc: '슬롯 보상 2배' },
    };

    const CHARM_PRICES = { common: 2, rare: 5, epic: 10, legendary: 20 };
    const showCharmShop = ref(false);

    const activeCharms = computed(() => {
      try { return JSON.parse(myData.value.active_charms || '[]'); }
      catch { return []; }
    });
    const activeCharmsCount = computed(() => activeCharms.value.length);

    const charmShopItems = computed(() => {
      try { return JSON.parse(myData.value.charm_shop || '[]'); }
      catch { return []; }
    });

    const charmShopRefreshTime = computed(() => {
      if (!myData.value.charm_refresh_at) return '새로고침 필요';
      const refreshAt = new Date(myData.value.charm_refresh_at);
      const now = new Date();
      const diff = refreshAt - now;
      if (diff <= 0) return '새로고침 가능';
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      return `${mins}:${String(secs).padStart(2, '0')}`;
    });

    const totalCharmBonus = computed(() => {
      let bonus = 0;
      for (const charm of activeCharms.value) {
        const charmData = CHARMS[charm.id];
        if (charmData?.effect === 'slotWinRate') bonus += charmData.value;
      }
      return bonus;
    });

    function generateCharmShop() {
      const allCharms = Object.keys(CHARMS);
      const items = [];
      const rarityPool = ['common', 'common', 'rare', 'rare', 'epic', 'legendary'];
      
      for (let i = 0; i < 3; i++) {
        const targetRarity = rarityPool[Math.floor(Math.random() * rarityPool.length)];
        const candidates = allCharms.filter(id => CHARMS[id].rarity === targetRarity);
        const selectedId = candidates[Math.floor(Math.random() * candidates.length)];
        items.push({
          id: selectedId,
          rarity: CHARMS[selectedId].rarity,
          price: CHARM_PRICES[CHARMS[selectedId].rarity],
          sold: false
        });
      }
      
      myData.value.charm_shop = JSON.stringify(items);
      myData.value.charm_refresh_at = new Date(Date.now() + 60 * 60 * 1000).toISOString(); // 1시간 후
    }

    function refreshCharmShop() {
      if (myData.value.coins < 3) {
        showToast('코인이 부족합니다!', 'warning');
        return;
      }
      myData.value.coins -= 3;
      generateCharmShop();
      showToast('상점이 새로고침되었습니다!', 'success');
    }

    // 2. buyCharm 함수에 중복 체크 추가
    function buyCharm(idx) {
      const items = [...charmShopItems.value];
      const item = items[idx];
      if (!item || item.sold) return;
      if (myData.value.coins < item.price) {
        showToast('코인이 부족합니다!', 'warning');
        return;
      }

      // 중복 체크 추가
      const alreadyOwned = activeCharms.value.some(charm => charm.id === item.id);
      if (alreadyOwned) {
        showToast('이미 보유한 유물입니다!', 'warning');
        return;
      }

      myData.value.coins -= item.price;
      items[idx].sold = true;
      myData.value.charm_shop = JSON.stringify(items);

      const newCharms = [...activeCharms.value, { id: item.id, rarity: item.rarity }];
      myData.value.active_charms = JSON.stringify(newCharms);

      playCharmBuy();
      showToast(`${CHARMS[item.id].name} 획득!`, 'charm');
    }

    // ==================== 슬롯머신 ====================
    const SYMBOLS = {
      LION: { id: 'LION', name: '사자', img: '/imgs/cow_clicker/reel_symbol_G.png', weight: 20, tier: 'common', mult: 1 },
      CROW: { id: 'CROW', name: '까마귀', img: '/imgs/cow_clicker/reel_symbol_T.png', weight: 20, tier: 'common', mult: 1 },
      TURTLE: { id: 'TURTLE', name: '거북이', img: '/imgs/cow_clicker/reel_symbol_S.png', weight: 20, tier: 'common', mult: 1 },
      CAT: { id: 'CAT', name: '고양이', img: '/imgs/cow_clicker/reel_symbol_E.png', weight: 12, tier: 'rare', mult: 1.5 },
      DOG: { id: 'DOG', name: '강아지', img: '/imgs/cow_clicker/reel_symbol_L.png', weight: 12, tier: 'rare', mult: 1.5 },
      HARE: { id: 'HARE', name: '토끼', img: '/imgs/cow_clicker/reel_symbol_V.png', weight: 6, tier: 'epic', mult: 2 },
      ELEPHANT: { id: 'ELEPHANT', name: '코끼리', img: '/imgs/cow_clicker/reel_symbol_C.png', weight: 6, tier: 'epic', mult: 2 },
      MIRECAT: { id: 'MIRECAT', name: '미어캣', img: '/imgs/cow_clicker/reel_symbol_O.png', weight: 4, tier: 'legendary', mult: 3 },
    };
    const totalWeight = Object.values(SYMBOLS).reduce((sum, s) => sum + s.weight, 0);
    const TIER_INFO = [
      { tier: 'common', name: '일반', mult: 'x1' },
      { tier: 'rare', name: '레어', mult: 'x1.5' },
      { tier: 'epic', name: '에픽', mult: 'x2' },
      { tier: 'legendary', name: '전설', mult: 'x3' },
    ];
    const symbolsByTier = computed(() => {
      const grouped = {};
      for (const tier of TIER_INFO) grouped[tier.tier] = Object.values(SYMBOLS).filter(s => s.tier === tier.tier);
      return grouped;
    });
    const PATTERNS = [
      { cells: [0, 1, 2], mult: 1.5, name: '가로3', is4plus: false },
      { cells: [1, 2, 3], mult: 1.5, name: '가로3', is4plus: false },
      { cells: [2, 3, 4], mult: 1.5, name: '가로3', is4plus: false },
      { cells: [5, 6, 7], mult: 1.5, name: '가로3', is4plus: false },
      { cells: [6, 7, 8], mult: 1.5, name: '가로3', is4plus: false },
      { cells: [7, 8, 9], mult: 1.5, name: '가로3', is4plus: false },
      { cells: [10, 11, 12], mult: 1.5, name: '가로3', is4plus: false },
      { cells: [11, 12, 13], mult: 1.5, name: '가로3', is4plus: false },
      { cells: [12, 13, 14], mult: 1.5, name: '가로3', is4plus: false },
      { cells: [0, 5, 10], mult: 1.5, name: '세로3', is4plus: false },
      { cells: [1, 6, 11], mult: 1.5, name: '세로3', is4plus: false },
      { cells: [2, 7, 12], mult: 1.5, name: '세로3', is4plus: false },
      { cells: [3, 8, 13], mult: 1.5, name: '세로3', is4plus: false },
      { cells: [4, 9, 14], mult: 1.5, name: '세로3', is4plus: false },
      { cells: [0, 6, 12], mult: 1.5, name: '대각', is4plus: false },
      { cells: [1, 7, 13], mult: 1.5, name: '대각', is4plus: false },
      { cells: [2, 8, 14], mult: 1.5, name: '대각', is4plus: false },
      { cells: [10, 6, 2], mult: 1.5, name: '대각', is4plus: false },
      { cells: [11, 7, 3], mult: 1.5, name: '대각', is4plus: false },
      { cells: [12, 8, 4], mult: 1.5, name: '대각', is4plus: false },
      { cells: [0, 1, 2, 3], mult: 2.5, name: '가로4', is4plus: true },
      { cells: [1, 2, 3, 4], mult: 2.5, name: '가로4', is4plus: true },
      { cells: [5, 6, 7, 8], mult: 2.5, name: '가로4', is4plus: true },
      { cells: [6, 7, 8, 9], mult: 2.5, name: '가로4', is4plus: true },
      { cells: [10, 11, 12, 13], mult: 2.5, name: '가로4', is4plus: true },
      { cells: [11, 12, 13, 14], mult: 2.5, name: '가로4', is4plus: true },
      { cells: [0, 1, 2, 3, 4], mult: 5, name: '가로5', is4plus: true },
      { cells: [5, 6, 7, 8, 9], mult: 5, name: '가로5', is4plus: true },
      { cells: [10, 11, 12, 13, 14], mult: 5, name: '가로5', is4plus: true },
      { cells: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14], mult: 50, name: '풀그리드', is4plus: true },
    ];
    const PATTERN_INFO = [
      { name: '가로3', visual: '•••', mult: 1.5, is4plus: false },
      { name: '세로3', visual: '⋮', mult: 1.5, is4plus: false },
      { name: '대각3', visual: '⋱', mult: 1.5, is4plus: false },
      { name: '가로4', visual: '••••', mult: 2.5, is4plus: true },
      { name: '가로5', visual: '•••••', mult: 5, is4plus: true },
      { name: '풀그리드', visual: '▣', mult: 50, is4plus: true },
    ];

    // 슬롯 상태
    const slotGrid = ref(Array(15).fill('LION'));
    const slotBet = ref(10);
    const slotSpinning = ref(false);
    const slotResult = ref(null);
    const slotWinCells = ref([]);
    const slotMatches = ref([]);
    const slotBonus = ref(false);
    const showPatternOverlay = ref(false);

    // ==================== 이벤트 시스템 ====================
    const SLOT_EVENTS = {
      jackpotChance: { 
        id: 'jackpotChance', 
        name: '잭팟 찬스', 
        icon: '🎯', 
        desc: '풀그리드 확률 5%!', 
        type: 'good', 
        color: '#ffd700',
        boost: { fullgrid: 0.05 }
      },
      fireSpin: { 
        id: 'fireSpin', 
        name: '파이어 스핀', 
        icon: '🔥', 
        desc: '5매칭 확률 10%!', 
        type: 'good', 
        color: '#ef4444',
        boost: { line5: 0.1 }
      },
      luckySpin: { 
        id: 'luckySpin', 
        name: '럭키 스핀', 
        icon: '⚡', 
        desc: '4매칭 확률 20%!', 
        type: 'good', 
        color: '#22c55e',
        boost: { line4: 0.2 }
      },
    };

    const currentEvent = ref(null);
    const eventVisible = ref(false);
    const eventAnimating = ref(false);
    const lockedCells = ref([]);
    const wildCells = ref([]);
    const isDoubleChance = ref(false);

    const isHighRiskBet = computed(() => {
      if (myData.value.score <= 0) return false;
      const betRatio = slotBet.value / myData.value.score;
      return betRatio >= 0.1 || slotBet.value >= 100000000;
    });

    function rollEvent() {
      if (!isHighRiskBet.value) return null;
      if (Math.random() > 0.1) return null;
      const events = Object.values(SLOT_EVENTS);
      return events[Math.floor(Math.random() * events.length)];
    }

    function getRandomSymbol(event = null) {
      if (event?.id === 'cursedSpin') {
        const cursedSymbols = Object.values(SYMBOLS).filter(s => s.tier === 'common' || s.tier === 'rare');
        const cursedWeight = cursedSymbols.reduce((sum, s) => sum + s.weight, 0);
        let rand = Math.random() * cursedWeight;
        for (const sym of cursedSymbols) {
          rand -= sym.weight;
          if (rand <= 0) return sym.id;
        }
        return 'LION';
      }
      let rand = Math.random() * totalWeight;
      for (const sym of Object.values(SYMBOLS)) {
        rand -= sym.weight;
        if (rand <= 0) return sym.id;
      }
      return 'LION';
    }

    function checkPatterns() {
      const matches = [];
      const checkedPatterns = new Set();
      for (const pattern of PATTERNS) {
        const symbols = pattern.cells.map(i => slotGrid.value[i]);
        const nonWildSymbols = symbols.filter(s => s !== 'WILD');
        if (nonWildSymbols.length === 0) {
          const key = `${pattern.name}-WILD`;
          if (!checkedPatterns.has(key)) {
            checkedPatterns.add(key);
            matches.push({ pattern, symbol: 'MIRECAT', cells: [...pattern.cells] });
          }
          continue;
        }
        const first = nonWildSymbols[0];
        const allMatch = nonWildSymbols.every(s => s === first);
        if (allMatch) {
          const key = `${pattern.name}-${first}`;
          if (!checkedPatterns.has(key)) {
            checkedPatterns.add(key);
            matches.push({ pattern, symbol: first, cells: [...pattern.cells] });
          }
        }
      }
      return matches;
    }

    function calculateWinnings(matches, bet, event = null) {
      if (matches.length === 0) return { totalWin: 0, matches: [], hasBonus: false, eventEffect: null, charmEffect: null };
      const hasBonus = matches.some(m => m.pattern.is4plus);
      let totalWin = 0;
      const processedMatches = [];
      
      // Charm 보너스 배율
      let charmMult = 1;
      for (const charm of activeCharms.value) {
        const charmData = CHARMS[charm.id];
        if (charmData?.effect === 'bonusMult') charmMult += charmData.value;
        if (charmData?.effect === 'allBoost') charmMult += charmData.value;
      }
      
      // 프레스티지 보너스
      const prestigeMult = 1 + (myData.value.prestige_level || 0) * 0.05;
      
      for (const match of matches) {
        const symbolData = SYMBOLS[match.symbol];
        const symbolMult = symbolData?.mult || 1;
        let baseMult = match.pattern.mult * symbolMult;
        const finalMult = hasBonus ? baseMult * 2 : baseMult;
        const payout = Math.floor(bet * finalMult * charmMult * prestigeMult);
        totalWin += payout;
        processedMatches.push({ ...match, symbolMult, finalMult, payout });
      }
      
      let charmEffect = null;
      if (charmMult > 1) {
        charmEffect = `배율 x${charmMult.toFixed(1)}`;
      }
      
      let eventEffect = null;
      if (event?.id === 'dragonWrath' && totalWin > 0) {
        const tax = Math.floor(totalWin * 0.3);
        totalWin -= tax;
        eventEffect = `🐉 용의 세금: -${formatNumber(tax)}`;
      }
      
      return { totalWin, matches: processedMatches, hasBonus, eventEffect, charmEffect };
    }

    function getWinClass() {
      if (!slotResult.value) return '';
      const mult = slotResult.value.totalWin / slotBet.value;
      if (mult >= 20) return 'jackpot-win';
      if (mult >= 10) return 'big-win';
      if (mult >= 5) return 'nice-win';
      return 'small-win';
    }
    function getWinLabel() {
      if (!slotResult.value) return 'WIN!';
      const mult = slotResult.value.totalWin / slotBet.value;
      if (mult >= 20) return 'JACKPOT!';
      if (mult >= 10) return 'BIG WIN!';
      if (mult >= 5) return 'NICE!';
      if (mult >= 2) return 'WIN!';
      return 'WIN';
    }

    // ==================== 슬롯 해금 & 배팅 ====================
    const isSlotUnlocked = computed(() => (goldUpgradesData.value.slotUnlock || 0) >= 1);
    const BET_LIMITS = [10000, 100000, 1000000, 10000000, 100000000, 10000000000];
    const maxBetAmount = computed(() => {
      const level = goldUpgradesData.value.betLimit || 0;
      return BET_LIMITS[level] || BET_LIMITS[0];
    });

    function handleSlotTabClick() {
      if (!isSlotUnlocked.value) { playLocked(); showToast('황금상점에서 도박장을 해금하세요!', 'warning'); return; }
      currentTab.value = 'slot';
    }
    function adjustBet(amount) {
      const maxBet = Math.min(myData.value.score, maxBetAmount.value);
      slotBet.value = Math.max(1, Math.min(maxBet, slotBet.value + amount));
    }
    function setBet(amount) {
      const maxBet = Math.min(myData.value.score, maxBetAmount.value);
      slotBet.value = Math.max(1, Math.min(maxBet, amount));
    }
    function clampBet() {
      const maxBet = Math.min(myData.value.score, maxBetAmount.value);
      slotBet.value = Math.max(1, Math.min(maxBet, slotBet.value));
    }

    function spinSlot() {
      if (slotSpinning.value || eventAnimating.value) return;
      if (myData.value.score < slotBet.value || slotBet.value < 1) return;
      if (slotBet.value > maxBetAmount.value) { showToast(`최대 배팅: ${formatNumber(maxBetAmount.value)}`, 'warning'); return; }

      myData.value.score -= slotBet.value;
      currentEvent.value = null;
      
      const event = rollEvent();
      if (event) {
        currentEvent.value = event;
        eventAnimating.value = true;
        eventVisible.value = true;
        if (event.type === 'good') playEventGood(); else { playEventBad(); triggerScreenShake(0.5, 300); }
        setTimeout(() => {
          eventVisible.value = false;
          setTimeout(() => { eventAnimating.value = false; applyEventAndSpin(event); }, 300);
        }, 1500);
        return;
      }
      applyEventAndSpin(null);
    }

    function applyEventAndSpin(event) {
      playSpinStart();
      slotSpinning.value = true;
      slotResult.value = null;
      slotWinCells.value = [];
      slotMatches.value = [];
      slotBonus.value = false;
      showPatternOverlay.value = false;

      let spinCount = 0;
      const spinInterval = setInterval(() => {
        for (let i = 0; i < 15; i++) {
          slotGrid.value[i] = getRandomSymbol(event);
        }
        slotGrid.value = [...slotGrid.value];
        spinCount++;
        if (spinCount % 3 === 0) playSpinTick();
        if (spinCount >= 25) {
          clearInterval(spinInterval);
          playSpinStop();
          setTimeout(() => finishSpin(event), 150);
        }
      }, 60);
    }

    function finishSpin(event) {
      // Charm 보너스 계산
      let charmLine3Bonus = 0, charmLine4Bonus = 0, charmLine5Bonus = 0, charmJackpotBonus = 0;
      for (const charm of activeCharms.value) {
        const charmData = CHARMS[charm.id];
        if (charmData?.effect === 'line3Rate') charmLine3Bonus += charmData.value;
        if (charmData?.effect === 'line4Rate') charmLine4Bonus += charmData.value;
        if (charmData?.effect === 'line5Rate') charmLine5Bonus += charmData.value;
        if (charmData?.effect === 'jackpotRate') charmJackpotBonus += charmData.value;
        if (charmData?.effect === 'slotWinRate') {
          charmLine3Bonus += charmData.value * 0.5;
          charmLine4Bonus += charmData.value * 0.3;
        }
      }
      
      // 캐릭터 특성 보너스
      const charTrait = CHARACTER_TRAITS[myData.value.type];
      if (charTrait?.passive.id === 'slotBoost') {
        charmLine4Bonus += charTrait.passive.value;
      }
      
      // 정복 버프
      charmLine4Bonus += conquestBuffs.value.slot;
      
      // 스킬 효과 체크
      if (activeSkillEffect.value?.effect === 'slotGuarantee') {
        charmLine4Bonus += activeSkillEffect.value.value;
      }
      
      // 확률 부스트 이벤트 처리
      const jackpotRoll = Math.random();
      let forcedSymbol = null;
      let forcedPattern = null;
      
      let fullgridChance = 0.0001 + charmJackpotBonus;  // 0.05% → 0.01%
      let line5Chance = 0.002 + charmLine5Bonus;         // 0.5% → 0.2%
      let line4Chance = 0.015 + charmLine4Bonus;         // 3% → 1.5%
      let line3Chance = 0.08 + charmLine3Bonus;          // 15% → 8%
      
      if (event?.boost) {
        if (event.boost.fullgrid) fullgridChance = event.boost.fullgrid;
        if (event.boost.line5) line5Chance = event.boost.line5;
        if (event.boost.line4) line4Chance = event.boost.line4;
      }
      
      if (jackpotRoll < fullgridChance) {
        forcedPattern = 'fullgrid';
        const symbols = Object.keys(SYMBOLS);
        forcedSymbol = symbols[Math.floor(Math.random() * symbols.length)];
      } else if (jackpotRoll < fullgridChance + line5Chance) {
        forcedPattern = 'line5';
        const symbols = Object.keys(SYMBOLS);
        forcedSymbol = symbols[Math.floor(Math.random() * symbols.length)];
      } else if (jackpotRoll < fullgridChance + line5Chance + line4Chance) {
        forcedPattern = 'line4';
        const symbols = Object.keys(SYMBOLS);
        forcedSymbol = symbols[Math.floor(Math.random() * symbols.length)];
      } else if (jackpotRoll < fullgridChance + line5Chance + line4Chance + line3Chance) {
        forcedPattern = 'line3';
        const symbols = Object.keys(SYMBOLS);
        forcedSymbol = symbols[Math.floor(Math.random() * symbols.length)];
      }
      
      if (forcedPattern === 'fullgrid') {
        for (let i = 0; i < 15; i++) {
          slotGrid.value[i] = forcedSymbol;
        }
      } else if (forcedPattern === 'line5') {
        const row = Math.floor(Math.random() * 3);
        const startIdx = row * 5;
        for (let i = 0; i < 5; i++) {
          slotGrid.value[startIdx + i] = forcedSymbol;
        }
        for (let i = 0; i < 15; i++) {
          if (i >= startIdx && i < startIdx + 5) continue;
          slotGrid.value[i] = getRandomSymbol(event);
        }
      } else if (forcedPattern === 'line4') {
        const row = Math.floor(Math.random() * 3);
        const startIdx = row * 5;
        const offset = Math.random() < 0.5 ? 0 : 1;
        for (let i = 0; i < 4; i++) {
          slotGrid.value[startIdx + offset + i] = forcedSymbol;
        }
        for (let i = 0; i < 15; i++) {
          if (i >= startIdx + offset && i < startIdx + offset + 4) continue;
          slotGrid.value[i] = getRandomSymbol(event);
        }
      } else if (forcedPattern === 'line3') {
        const row = Math.floor(Math.random() * 3);
        const startIdx = row * 5;
        const offset = Math.floor(Math.random() * 3);
        for (let i = 0; i < 3; i++) {
          slotGrid.value[startIdx + offset + i] = forcedSymbol;
        }
        for (let i = 0; i < 15; i++) {
          if (i >= startIdx + offset && i < startIdx + offset + 3) continue;
          slotGrid.value[i] = getRandomSymbol(event);
        }
      } else {
        for (let i = 0; i < 15; i++) {
          slotGrid.value[i] = getRandomSymbol(event);
        }
      }
      
      slotGrid.value = [...slotGrid.value];
      slotSpinning.value = false;

      const matches = checkPatterns();
      const result = calculateWinnings(matches, slotBet.value, event);
      
      if (event?.boost && result.totalWin > 0) {
        result.eventEffect = `${event.icon} ${event.name} 발동!`;
      }
      
      const winCells = new Set();
      for (const match of matches) match.cells.forEach(c => winCells.add(c));
      slotWinCells.value = Array.from(winCells);
      slotMatches.value = result.matches;
      slotBonus.value = result.hasBonus;
      
      if (result.totalWin > 0) {
        myData.value.score += result.totalWin;
        checkConquest();
        const multiplier = result.totalWin / slotBet.value;
        if (multiplier >= 20) { playWinJackpot(); setTimeout(() => playCoinExplosion(), 400); triggerJackpot(); showToast(`JACKPOT! +${formatNumber(result.totalWin)}`, 'jackpot'); }
        else if (multiplier >= 10) { playWinBig(); setTimeout(() => playCoinShower(), 300); triggerBigWin(); showToast(`BIG WIN! +${formatNumber(result.totalWin)}`, 'bigwin'); }
        else if (multiplier >= 5) { playWinMedium(); playChaChing(); setTimeout(() => playCoinDrop(6), 150); triggerNiceWin(); showToast(`NICE! +${formatNumber(result.totalWin)}`, 'nice'); }
        else if (multiplier >= 2) { playWinSmall(); playChaChing(); setTimeout(() => playCoinDrop(3), 100); triggerMediumWin(); if (result.hasBonus) { playBonus(); showToast(`BONUS! +${formatNumber(result.totalWin)}`, 'bonus'); } else showToast(`WIN! +${formatNumber(result.totalWin)}`, 'win'); }
        else { playWinTiny(); triggerSmallWin(); showToast(`+${formatNumber(result.totalWin)}`, 'small'); }
      } else {
        playLose();
      }
      slotResult.value = result;
      if (result.totalWin > 0) {
        setTimeout(() => { showPatternOverlay.value = true; }, 300);
        setTimeout(() => { slotResult.value = null; }, 1500);
      }
      
      currentEvent.value = null;
      lockedCells.value = [];
      wildCells.value = [];
    }

    // ==================== 콤보 시스템 ====================
    const combo = ref(0);
    const comboTimeLeft = ref(0);
    const BASE_COMBO_DURATION = 1500;
    let comboTimer = null;
    let comboInterval = null;

    const comboDuration = computed(() => {
      let duration = BASE_COMBO_DURATION;
      // 캐릭터 특성
      const trait = CHARACTER_TRAITS[myData.value.type];
      if (trait?.passive.id === 'comboKeep') {
        duration *= (1 + trait.passive.value);
      }
      // 정복 버프
      duration *= (1 + conquestBuffs.value.combo);
      // 스킬 효과
      if (activeSkillEffect.value?.effect === 'comboFreeze') {
        duration = 999999;
      }
      return duration;
    });

    const comboMultiplier = computed(() => {
      let base = 1.0;
      if (combo.value >= 100) base = 3.0;
      else if (combo.value >= 50) base = 2.5;
      else if (combo.value >= 30) base = 2.0;
      else if (combo.value >= 15) base = 1.5;
      else if (combo.value >= 5) base = 1.2;
      
      // Charm 보너스
      for (const charm of activeCharms.value) {
        const charmData = CHARMS[charm.id];
        if (charmData?.effect === 'allBoost') base += charmData.value;
      }
      
      return base;
    });
    const comboClass = computed(() => {
      if (combo.value >= 100) return 'legendary';
      if (combo.value >= 50) return 'epic';
      if (combo.value >= 30) return 'rare';
      if (combo.value >= 15) return 'uncommon';
      return 'common';
    });
    const comboPercent = computed(() => Math.max(0, (comboTimeLeft.value / comboDuration.value) * 100));

    function addCombo() {
      combo.value++;
      comboTimeLeft.value = comboDuration.value;
      if (comboTimer) clearTimeout(comboTimer);
      if (comboInterval) clearInterval(comboInterval);
      comboInterval = setInterval(() => {
        comboTimeLeft.value -= 50;
        if (comboTimeLeft.value <= 0) clearInterval(comboInterval);
      }, 50);
      comboTimer = setTimeout(() => { combo.value = 0; comboTimeLeft.value = 0; }, comboDuration.value);
    }

    // ==================== 주간 시스템 & 프레스티지 ====================
    function getWeekString(date = new Date()) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 4 - (d.getDay() || 7));
      const yearStart = new Date(d.getFullYear(), 0, 1);
      const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      return `${d.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;
    }

    const currentWeek = computed(() => getWeekString());
    const currentWeekLabel = computed(() => {
      const [year, week] = currentWeek.value.split('-W');
      return `${year}년 ${week}주차`;
    });

    // 결산 상태
    const showSettlement = ref(false);
    const settlementRanking = ref([]);
    const towerAnimated = ref(false);
    const mySettlementRank = ref(null);
    const settlementProcessing = ref(false);
    const pendingConquest = ref(false);

    // 프레스티지 상태
    const showPrestigeModal = ref(false);
    const prestigeProcessing = ref(false);
    const canPrestige = computed(() => conqueredTypes.value.length >= 8);

    async function doPrestige() {
      if (!canPrestige.value || prestigeProcessing.value) return;
      prestigeProcessing.value = true;

      try {
        myData.value.prestige_level = (myData.value.prestige_level || 0) + 1;
        myData.value.score = 0;
        myData.value.coins = 0;
        myData.value.upgrades = '{}';
        myData.value.gold_upgrades = '{}';
        myData.value.conquered_types = '';
        myData.value.all_clear = 0;
        myData.value.active_charms = '[]';
        
        // 새 캐릭터 랜덤 선택
        myData.value.type = typeEx[Math.floor(Math.random() * typeEx.length)];
        
        await saveData();
        showPrestigeModal.value = false;
        showToast(`프레스티지 Lv.${myData.value.prestige_level}! 🎉`, 'prestige');
        triggerFireworks();
      } catch (e) {
        console.error(e);
        showToast('오류 발생', 'warning');
      } finally {
        prestigeProcessing.value = false;
      }
    }

    // 정복 버프 계산
    const conqueredTypes = computed(() => {
      const str = myData.value.conquered_types || '';
      return str ? str.split(',').filter(t => t) : [];
    });
    const isAllCleared = computed(() => conqueredTypes.value.length >= 8);

    const conquestBuffs = computed(() => {
      const buffs = { click: 0, auto: 0, combo: 0, crit: 0, golden: 0, coin: 0, slot: 0, exp: 0 };
      for (const t of conqueredTypes.value) {
        const trait = CHARACTER_TRAITS[t];
        if (trait?.conquest) {
          buffs[trait.conquest.id] += trait.conquest.value;
        }
      }
      return buffs;
    });

    const conquestVisible = ref(false);
    const newCharacterType = ref('');
    const conqueredCharName = ref('');
    const lastConqueredType = ref('');

    // 기록
    const historyList = ref([]);

    async function checkWeeklyReset() {
      const lastWeek = myData.value.last_reset_week || '';
      const thisWeek = currentWeek.value;
      
      if (lastWeek && lastWeek !== thisWeek) {
        await prepareSettlement();
      } else if (!lastWeek) {
        myData.value.last_reset_week = thisWeek;
      }
    }

    async function prepareSettlement() {
      const rows = await sql_query(`SELECT mb_id, score, type FROM cow_clicker WHERE score > 0 ORDER BY score DESC LIMIT 10`);
      
      if (rows.length === 0) {
        myData.value.last_reset_week = currentWeek.value;
        // Charm 초기화
        myData.value.active_charms = '[]';
        return;
      }

      const maxScore = Math.max(...rows.map(r => Number(r.score) || 0), 1);
      settlementRanking.value = rows.map((r) => ({
        ...r,
        score: Number(r.score) || 0,
        heightPercent: Math.max(10, Math.floor((Number(r.score) / maxScore) * 100))
      }));

      const myIdx = rows.findIndex(r => r.mb_id === myData.value.mb_id);
      mySettlementRank.value = myIdx >= 0 ? myIdx + 1 : null;

      if (myData.value.score >= 10_000_000_000_000) {
        const conquered = conqueredTypes.value;
        if (!conquered.includes(myData.value.type)) {
          pendingConquest.value = true;
        }
      }

      showSettlement.value = true;
      setTimeout(() => { towerAnimated.value = true; }, 500);
    }

    async function confirmSettlement() {
      if (settlementProcessing.value) return;
      settlementProcessing.value = true;

      try {
        const mbId = myData.value.mb_id;
        const lastWeek = getWeekString(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));

        if (myData.value.score > 0) {
          const rank = mySettlementRank.value || 999;
          await sql_query(`
            INSERT IGNORE INTO cow_clicker_history (week_year, mb_id, rank_position, final_score, character_type, conquered_this_week)
            VALUES ('${lastWeek}', '${mbId}', ${rank}, ${myData.value.score}, '${myData.value.type}', ${pendingConquest.value ? 1 : 0})
          `);
        }

        if (pendingConquest.value) {
          const newConquered = [...conqueredTypes.value, myData.value.type];
          myData.value.conquered_types = newConquered.join(',');
          conqueredCharName.value = CHARACTER_NAMES[myData.value.type];
          lastConqueredType.value = myData.value.type;

          if (newConquered.length >= 8) {
            myData.value.all_clear = 1;
            newCharacterType.value = '';
          } else {
            const available = typeEx.filter(t => !newConquered.includes(t));
            const newType = available[Math.floor(Math.random() * available.length)];
            myData.value.type = newType;
            newCharacterType.value = newType;
          }
          
          setTimeout(() => { conquestVisible.value = true; }, 300);
        }

        // 주간 초기화
        myData.value.score = 0;
        myData.value.upgrades = '{}';
        myData.value.last_reset_week = currentWeek.value;
        myData.value.active_charms = '[]'; // Charm 초기화

        await saveData();
        await loadRanking();

        showSettlement.value = false;
        towerAnimated.value = false;
        pendingConquest.value = false;
        
        showToast('🎉 새 시즌 시작!', 'event');
      } catch (e) {
        console.error(e);
        showToast('오류 발생', 'warning');
      } finally {
        settlementProcessing.value = false;
      }
    }

    async function loadHistory() {
      if (!myData.value.mb_id) return;
      const rows = await sql_query(`SELECT * FROM cow_clicker_history WHERE mb_id = '${myData.value.mb_id}' ORDER BY week_year DESC LIMIT 20`);
      historyList.value = rows;
    }

    const MAX_LEVEL_SCORE = 10_000_000_000_000;

    function checkConquest() {
      if (myData.value.score >= MAX_LEVEL_SCORE) {
        const conquered = conqueredTypes.value;
        if (!conquered.includes(myData.value.type)) {
          const newConquered = [...conquered, myData.value.type];
          myData.value.conquered_types = newConquered.join(',');
          conqueredCharName.value = CHARACTER_NAMES[myData.value.type];
          lastConqueredType.value = myData.value.type;

          if (newConquered.length >= 8) {
            myData.value.all_clear = 1;
            newCharacterType.value = '';
          } else {
            const available = typeEx.filter(t => !newConquered.includes(t));
            const newType = available[Math.floor(Math.random() * available.length)];
            myData.value.type = newType;
            newCharacterType.value = newType;
          }
          
          myData.value.score = 0;
          myData.value.upgrades = '{}';
          myData.value.coins = 0;           // 추가
          myData.value.gold_upgrades = '{}'; // 추가
          myData.value.active_charms = '[]';
          
          conquestVisible.value = true;
          saveData();
        }
      }
    }

    // ==================== 황금소 ====================
    const goldenCow = ref({ active: false, x: 50, y: 30, timeLeft: 0, reward: 1 });
    let goldenTimer = null;
    let goldenCountdown = null;

    function spawnGoldenCow() {
      if (goldenCow.value.active) return;
      
      playChaChing(); playCoinDrop(4);
      // 기본 보상
      let baseReward = 1 + (goldUpgradesData.value.goldenBonus || 0);
      
      // 캐릭터 특성
      const trait = CHARACTER_TRAITS[myData.value.type];
      if (trait?.passive.id === 'coinBonus') baseReward += trait.passive.value;
      
      // 정복 버프
      baseReward += conquestBuffs.value.coin;
      
      // Charm 보너스
      for (const charm of activeCharms.value) {
        const charmData = CHARMS[charm.id];
        if (charmData?.effect === 'coinBonus') baseReward += charmData.value;
      }
      
      // 프레스티지 보너스
      baseReward = Math.floor(baseReward * (1 + (myData.value.prestige_level || 0) * 0.1));
      
      const bonusChance = (goldUpgradesData.value.goldenLuck || 0) * 0.1;
      const extraReward = Math.random() < bonusChance ? 1 : 0;
      
      goldenCow.value = { 
        active: true, 
        x: 15 + Math.random() * 70, 
        y: 20 + Math.random() * 40, 
        timeLeft: 5, 
        reward: baseReward + extraReward 
      };
      
      goldenCountdown = setInterval(() => {
        goldenCow.value.timeLeft--;
        if (goldenCow.value.timeLeft <= 0) { 
          clearInterval(goldenCountdown); 
          goldenCow.value.active = false; 
        }
      }, 1000);
    }

    function catchGoldenCow(event) {
      if (!goldenCow.value.active) return;
      event.stopPropagation();
      clearInterval(goldenCountdown);
      myData.value.coins = (myData.value.coins || 0) + goldenCow.value.reward;
      playChaChing(); playCoinDrop(3);
      const id = Date.now();
      floatingTexts.value.push({ id, amount: goldenCow.value.reward, coin: true, x: 100 + Math.random() * 50, y: 80 + Math.random() * 40 });
      setTimeout(() => { floatingTexts.value = floatingTexts.value.filter(t => t.id !== id); }, 1000);
      showToast(`+${goldenCow.value.reward} 코인!`, 'coin');
      goldenCow.value.active = false;
    }

    function startGoldenTimer() {
      goldenTimer = setInterval(() => {
        // 기본 확률
        let spawnChance = 0.08 + (goldUpgradesData.value.goldenLuck || 0) * 0.02;
        
        // 캐릭터 특성
        const trait = CHARACTER_TRAITS[myData.value.type];
        if (trait?.passive.id === 'goldenChance') spawnChance += trait.passive.value;
        
        // 정복 버프
        spawnChance += conquestBuffs.value.golden;
        
        // Charm 보너스
        for (const charm of activeCharms.value) {
          const charmData = CHARMS[charm.id];
          if (charmData?.effect === 'goldenRate') spawnChance += charmData.value;
        }
        
        // 스킬 효과 (토끼)
        if (activeSkillEffect.value?.effect === 'goldenMagnet') {
          spawnChance = 1;
        }
        
        if (!goldenCow.value.active && Math.random() < spawnChance) spawnGoldenCow();
      }, 5000);
    }

    // ==================== 레벨 시스템 ====================
    const levels = [
      { name:"알", needs:0, pic:"/imgs/cow_clicker/ch_egg.png", mapNo:0 },
      { name:"깨어난 씨앗", needs:100, pic:"/imgs/cow_clicker/ch_LV5", mapNo:0 },
      { name:"작은 모험가", needs:1_000, pic:"/imgs/cow_clicker/ch_LV6", mapNo:0 },
      { name:"수련생", needs:10_000, pic:"/imgs/cow_clicker/ch_LV7", mapNo:1 },
      { name:"개척자", needs:100_000, pic:"/imgs/cow_clicker/ch_LV8", mapNo:1 },
      { name:"길잡이", needs:1_000_000, pic:"/imgs/cow_clicker/ch_LV9", mapNo:2 },
      { name:"숙련자", needs:10_000_000, pic:"/imgs/cow_clicker/ch_LV10", mapNo:2 },
      { name:"거대한 흐름의 주인", needs:100_000_000, pic:"/imgs/cow_clicker/ch_LV11", mapNo:3 },
      { name:"영역 관리자", needs:300_000_000, pic:"/imgs/cow_clicker/ch_LV12", mapNo:3 },
      { name:"도시의 수호자", needs:1_000_000_000, pic:"/imgs/cow_clicker/ch_LV13", mapNo:4 },
      { name:"대륙 설계자", needs:3_000_000_000, pic:"/imgs/cow_clicker/ch_LV14", mapNo:4 },
      { name:"왕좌의 조율자", needs:10_000_000_000, pic:"/imgs/cow_clicker/ch_LV15", mapNo:5 },
      { name:"황금 혈통", needs:50_000_000_000, pic:"/imgs/cow_clicker/ch_LV16", mapNo:5 },
      { name:"전설의 계승자", needs:100_000_000_000, pic:"/imgs/cow_clicker/ch_LV16", mapNo:5 },
      { name:"기원(Origin)", needs:10_000_000_000_000, pic:"/imgs/cow_clicker/ch_LV16", mapNo:5 }
    ];
    const maps = ["/imgs/cow_clicker/bg_0.png","/imgs/cow_clicker/bg_1.png","/imgs/cow_clicker/bg_2.png","/imgs/cow_clicker/bg_3.png","/imgs/cow_clicker/bg_4.png","/imgs/cow_clicker/bg_5.png"];

    const currentLevelIndex = computed(() => {
      const score = myData.value.score;
      for (let i = levels.length - 1; i >= 0; i--) if (score >= levels[i].needs) return i;
      return 0;
    });
    const currentLevel = computed(() => {
      const base = levels[currentLevelIndex.value];
      const pic = base.pic.includes("egg") ? base.pic : `${base.pic}${myData.value.type || 'G'}.png`;
      return { ...base, pic };
    });
    const currentMap = computed(() => maps[currentLevel.value.mapNo]);
    const nextLevelName = computed(() => {
      const next = levels[currentLevelIndex.value + 1];
      return next ? next.name : '최고 레벨!';
    });
    const levelPercent = computed(() => {
      const curr = levels[currentLevelIndex.value];
      const next = levels[currentLevelIndex.value + 1];
      if (!next) return 100;
      const progress = myData.value.score - curr.needs;
      const total = next.needs - curr.needs;
      return Math.min(100, Math.floor((progress / total) * 100));
    });

    // ==================== 상점 ====================
    const shopUpgrades = [
      { id: 'click', name: '클릭 파워', icon: '👆', desc: '클릭당 +1', maxLevel: 100, baseCost: 10, costMult: 1.12 },
      { id: 'auto', name: '자동 클릭', icon: '🤖', desc: '초당 +1 자동 획득', maxLevel: 50, baseCost: 100, costMult: 1.20 },
      { id: 'bonus', name: '보너스', icon: '🎁', desc: '전체 획득량 +10%', maxLevel: 20, baseCost: 500, costMult: 1.40 },
      { id: 'combo', name: '콤보 지속', icon: '🔥', desc: '콤보 유지시간 +0.2초', maxLevel: 10, baseCost: 300, costMult: 1.50 },
    ];
    const myUpgrades = computed(() => { try { return JSON.parse(myData.value.upgrades || '{}'); } catch { return {}; } });
    function getLevel(id) { return myUpgrades.value[id] || 0; }
    function getShopCost(upgrade) { return Math.floor(upgrade.baseCost * Math.pow(upgrade.costMult, getLevel(upgrade.id))); }
    function canAffordShop(upgrade) { return myData.value.score >= getShopCost(upgrade); }
    function isMaxed(upgrade) { return getLevel(upgrade.id) >= upgrade.maxLevel; }
    function buyShopUpgrade(upgrade) {
      if (!canAffordShop(upgrade) || isMaxed(upgrade)) return;
      myData.value.score -= getShopCost(upgrade);
      const newUpgrades = { ...myUpgrades.value };
      newUpgrades[upgrade.id] = (newUpgrades[upgrade.id] || 0) + 1;
      myData.value.upgrades = JSON.stringify(newUpgrades);
      showToast(upgrade.name + ' Lv.' + newUpgrades[upgrade.id] + '!', 'upgrade');
    }

    // ==================== 황금상점 ====================
    const goldUpgrades = [
      { id: 'slotUnlock', name: '도박장 해금', icon: '🎰', desc: '슬롯머신 이용 가능', maxLevel: 1, baseCost: 10, featured: true },
      { id: 'betLimit', name: '배팅 한도', icon: '💎', desc: '최대 배팅 금액 증가', maxLevel: 5, baseCost: 5, costMult: 2 },
      { id: 'goldenBonus', name: '황금 보너스', icon: '💰', desc: '황금소 코인 +1', maxLevel: 5, baseCost: 3 },
      { id: 'goldenLuck', name: '황금 행운', icon: '🍀', desc: '황금소 출현 확률 +2%', maxLevel: 5, baseCost: 5 },
      { id: 'critChance', name: '크리티컬', icon: '⚡', desc: '크리티컬 확률 +5%', maxLevel: 10, baseCost: 4 },
      { id: 'critDamage', name: '크리티컬 데미지', icon: '💥', desc: '크리티컬 배율 +0.5', maxLevel: 5, baseCost: 8 },
      { id: 'comboBonus', name: '콤보 보너스', icon: '🔥', desc: '콤보 배율 +0.1', maxLevel: 10, baseCost: 6 },
    ];
    const goldUpgradesData = computed(() => { try { return JSON.parse(myData.value.gold_upgrades || '{}'); } catch { return {}; } });
    function getGoldLevel(id) { return goldUpgradesData.value[id] || 0; }
    function getGoldCost(upgrade) {
      if (upgrade.costMult) return Math.floor(upgrade.baseCost * Math.pow(upgrade.costMult, getGoldLevel(upgrade.id)));
      return upgrade.baseCost;
    }
    function canAffordGold(upgrade) { return (myData.value.coins || 0) >= getGoldCost(upgrade); }
    function isGoldMaxed(upgrade) { return getGoldLevel(upgrade.id) >= upgrade.maxLevel; }
    function getGoldUpgradeDesc(upgrade) {
      if (upgrade.id === 'betLimit') {
        const level = getGoldLevel(upgrade.id);
        if (level >= upgrade.maxLevel) return `최대 한도: ${formatNumber(BET_LIMITS[upgrade.maxLevel])}`;
        return `다음 한도: ${formatNumber(BET_LIMITS[level + 1])}`;
      }
      return upgrade.desc;
    }
    function buyGoldUpgrade(upgrade) {
      if (!canAffordGold(upgrade) || isGoldMaxed(upgrade)) return;
      myData.value.coins -= getGoldCost(upgrade);
      const newUpgrades = { ...goldUpgradesData.value };
      newUpgrades[upgrade.id] = (newUpgrades[upgrade.id] || 0) + 1;
      myData.value.gold_upgrades = JSON.stringify(newUpgrades);
      if (upgrade.id === 'slotUnlock') showToast(`도박장이 해금되었습니다!`, 'unlock');
      else if (upgrade.id === 'betLimit') showToast(`배팅 한도 Lv.${newUpgrades[upgrade.id]}!`, 'upgrade');
      else showToast(`${upgrade.name} Lv.${newUpgrades[upgrade.id]}!`, 'upgrade');
    }

    // ==================== 클릭 ====================
    const clickPower = computed(() => {
      let base = 1 + (myUpgrades.value.click || 0);
      let bonus = 1 + (myUpgrades.value.bonus || 0) * 0.1;
      const comboBonus = comboMultiplier.value + (goldUpgradesData.value.comboBonus || 0) * 0.1;
      
      // 캐릭터 특성
      const trait = CHARACTER_TRAITS[myData.value.type];
      if (trait?.passive.id === 'clickPower') bonus += trait.passive.value;
      if (trait?.passive.id === 'allBoost') bonus += trait.passive.value;
      
      // 정복 버프
      bonus += conquestBuffs.value.click + conquestBuffs.value.exp;
      
      // Charm 보너스
      for (const charm of activeCharms.value) {
        const charmData = CHARMS[charm.id];
        if (charmData?.effect === 'allBoost') bonus += charmData.value;
      }
      
      // 프레스티지 보너스
      const prestigeBonus = 1 + (myData.value.prestige_level || 0) * 0.1;
      
      // 스킬 효과 (미어캣)
      let skillMult = 1;
      if (activeSkillEffect.value?.effect === 'allMulti') {
        skillMult = activeSkillEffect.value.value;
      }
      
      return Math.floor(base * bonus * comboBonus * prestigeBonus * skillMult);
    });

    const autoClickPower = computed(() => {
      let base = myUpgrades.value.auto || 0;
      let bonus = 1 + (myUpgrades.value.bonus || 0) * 0.1;
      
      // 캐릭터 특성
      const trait = CHARACTER_TRAITS[myData.value.type];
      if (trait?.passive.id === 'autoBoost') bonus += trait.passive.value;
      if (trait?.passive.id === 'allBoost') bonus += trait.passive.value;
      
      // 정복 버프
      bonus += conquestBuffs.value.auto + conquestBuffs.value.exp;
      
      // Charm 보너스
      for (const charm of activeCharms.value) {
        const charmData = CHARMS[charm.id];
        if (charmData?.effect === 'autoSpeed') bonus += charmData.value;
        if (charmData?.effect === 'allBoost') bonus += charmData.value;
      }
      
      // 프레스티지 보너스
      const prestigeBonus = 1 + (myData.value.prestige_level || 0) * 0.1;
      
      // 스킬 효과
      let skillMult = 1;
      if (activeSkillEffect.value?.effect === 'autoMulti') {
        skillMult = activeSkillEffect.value.value;
      }
      if (activeSkillEffect.value?.effect === 'allMulti') {
        skillMult = activeSkillEffect.value.value;
      }
      
      return Math.floor(base * bonus * prestigeBonus * skillMult);
    });

    const critChance = computed(() => {
      let base = (goldUpgradesData.value.critChance || 0) * 5;
      
      // 캐릭터 특성
      const trait = CHARACTER_TRAITS[myData.value.type];
      if (trait?.passive.id === 'critChance') base += trait.passive.value * 100;
      
      // 정복 버프
      base += conquestBuffs.value.crit * 100;
      
      // Charm 보너스
      for (const charm of activeCharms.value) {
        const charmData = CHARMS[charm.id];
        if (charmData?.effect === 'critChance') base += charmData.value * 100;
      }
      
      // 스킬 효과 (사자)
      if (activeSkillEffect.value?.effect === 'critBoost') {
        base += activeSkillEffect.value.value * 100;
      }
      
      return base;
    });

    const critDamage = computed(() => {
      let base = 2 + (goldUpgradesData.value.critDamage || 0) * 0.5;
      
      // 스킬 효과 (고양이)
      if (activeSkillEffect.value?.effect === 'superCrit') {
        base *= activeSkillEffect.value.value;
      }
      
      return base;
    });

    function handleClick(event) {
      const isCrit = Math.random() * 100 < critChance.value;
      const basePoints = clickPower.value;
      const points = isCrit ? Math.floor(basePoints * critDamage.value) : basePoints;
      myData.value.score += points;
      checkConquest();
      addCombo();
      if (isCrit) { playTone(800, 0.1, 'sine', 0.2); playTone(1200, 0.15, 'sine', 0.25); }
      else playTone(600 + Math.random() * 200, 0.08, 'sine', 0.15);
      isShaking.value = true;
      setTimeout(() => { isShaking.value = false; }, 100);
      const rect = event.target.getBoundingClientRect();
      const id = Date.now() + Math.random();
      floatingTexts.value.push({ 
        id, 
        amount: points, 
        crit: isCrit, 
        skill: !!activeSkillEffect.value,
        x: event.clientX - rect.left + (Math.random() - 0.5) * 30, 
        y: event.clientY - rect.top - 10 
      });
      setTimeout(() => { floatingTexts.value = floatingTexts.value.filter(t => t.id !== id); }, 800);
    }

    let autoInterval = null;
    function startAutoClick() {
      if (autoInterval) clearInterval(autoInterval);
      autoInterval = setInterval(() => {
        if (autoClickPower.value > 0) {
          myData.value.score += autoClickPower.value;
          const id = Date.now();
          floatingTexts.value.push({ id, amount: autoClickPower.value, auto: true, x: 80 + Math.random() * 40, y: 120 + Math.random() * 40 });
          setTimeout(() => { floatingTexts.value = floatingTexts.value.filter(t => t.id !== id); }, 800);
        }
      }, 1000);
    }

    // ==================== 랭킹 ====================
    function getRankClass(index) { if (index === 0) return 'gold'; if (index === 1) return 'silver'; if (index === 2) return 'bronze'; return ''; }
    function getPlayerPic(player) {
      const score = player.score || 0;
      let levelIdx = 0;
      for (let i = levels.length - 1; i >= 0; i--) if (score >= levels[i].needs) { levelIdx = i; break; }
      const base = levels[levelIdx];
      return base.pic.includes("egg") ? base.pic : `${base.pic}${player.type || 'G'}.png`;
    }

    // ==================== DB ====================
    async function loadData() {
      const mbId = getMbid();
      let rows = await sql_query(`SELECT * FROM cow_clicker WHERE mb_id = '${mbId}'`);
      if (rows.length === 0) {
        const newType = typeEx[Math.floor(Math.random() * typeEx.length)];
        const thisWeek = currentWeek.value;
        await sql_query(`INSERT INTO cow_clicker (mb_id, score, coins, type, upgrades, gold_upgrades, last_reset_week, conquered_types, all_clear, prestige_level, active_charms, charm_shop, charm_refresh_at) VALUES ('${mbId}', 0, 0, '${newType}', '{}', '{}', '${thisWeek}', '', 0, 0, '[]', '[]', '')`);
        myData.value = { 
          mb_id: mbId, score: 0, coins: 0, type: newType, upgrades: '{}', gold_upgrades: '{}', 
          last_reset_week: thisWeek, conquered_types: '', all_clear: 0, prestige_level: 0,
          active_charms: '[]', charm_shop: '[]', charm_refresh_at: ''
        };
        generateCharmShop();
      } else {
        myData.value = { 
          ...rows[0], 
          coins: rows[0].coins || 0, 
          gold_upgrades: rows[0].gold_upgrades || '{}',
          last_reset_week: rows[0].last_reset_week || '',
          conquered_types: rows[0].conquered_types || '',
          all_clear: rows[0].all_clear || 0,
          prestige_level: rows[0].prestige_level || 0,
          active_charms: rows[0].active_charms || '[]',
          charm_shop: rows[0].charm_shop || '[]',
          charm_refresh_at: rows[0].charm_refresh_at || ''
        };
        
        // Charm 상점 자동 새로고침
        if (!myData.value.charm_refresh_at || new Date(myData.value.charm_refresh_at) <= new Date()) {
          generateCharmShop();
        }
        
        await checkWeeklyReset();
      }
    }

    async function loadRanking() {
      const rows = await sql_query(`SELECT mb_id, score, type, conquered_types, prestige_level FROM cow_clicker ORDER BY score DESC LIMIT 20`);
      rankingList.value = rows;
    }

    async function saveData() {
      const esc = {
        upgrades: (myData.value.upgrades || '{}').replace(/'/g, "''"),
        gold: (myData.value.gold_upgrades || '{}').replace(/'/g, "''"),
        conquered: (myData.value.conquered_types || '').replace(/'/g, "''"),
        charms: (myData.value.active_charms || '[]').replace(/'/g, "''"),
        shop: (myData.value.charm_shop || '[]').replace(/'/g, "''"),
        refreshAt: (myData.value.charm_refresh_at || '').replace(/'/g, "''")
      };
      await sql_query(`
        UPDATE cow_clicker SET 
          score = ${myData.value.score}, 
          coins = ${myData.value.coins || 0}, 
          type = '${myData.value.type}',
          upgrades = '${esc.upgrades}', 
          gold_upgrades = '${esc.gold}',
          last_reset_week = '${myData.value.last_reset_week || ''}',
          conquered_types = '${esc.conquered}',
          all_clear = ${myData.value.all_clear || 0},
          prestige_level = ${myData.value.prestige_level || 0},
          active_charms = '${esc.charms}',
          charm_shop = '${esc.shop}',
          charm_refresh_at = '${esc.refreshAt}'
        WHERE mb_id = '${myData.value.mb_id}'
      `);
    }

    // ==================== 유틸 ====================
    function formatNumber(n) {
      if (n === undefined || n === null) return '0';
      const absN = Math.abs(n);
      const sign = n < 0 ? '-' : '';
      if (absN >= 1e16) return sign + (absN / 1e16).toFixed(1) + '경';
      if (absN >= 1e12) return sign + (absN / 1e12).toFixed(1) + '조';
      if (absN >= 1e8) return sign + Math.floor(absN / 1e8) + '억';
      if (absN >= 1e4) return sign + Math.floor(absN / 1e4) + '만';
      if (absN >= 1e3) return sign + Math.floor(absN / 1e3) + '천';
      return sign + Math.floor(absN).toString();
    }

    function showToast(msg, type = '') {
      toastMsg.value = msg;
      toastType.value = type;
      toastVisible.value = true;
      setTimeout(() => { toastVisible.value = false; }, 2000);
    }

    useBackButton(() => { props.oth_showCowClicker(false); });
    function closeDialog() { props.oth_showCowClicker(false); }

    // ==================== 라이프사이클 ====================
    let saveInterval = null;
    let charmRefreshInterval = null;

    onMounted(async () => {
      await loadData();
      await loadRanking();
      await loadHistory();
      startAutoClick();
      startGoldenTimer();
      saveInterval = setInterval(async () => { await saveData(); await loadRanking(); }, 3000);
      
      // Charm 상점 자동 새로고침 체크
      charmRefreshInterval = setInterval(() => {
        if (myData.value.charm_refresh_at && new Date(myData.value.charm_refresh_at) <= new Date()) {
          generateCharmShop();
        }
      }, 60000);
    });

    onUnmounted(() => {
      if (saveInterval) clearInterval(saveInterval);
      if (autoInterval) clearInterval(autoInterval);
      if (goldenTimer) clearInterval(goldenTimer);
      if (goldenCountdown) clearInterval(goldenCountdown);
      if (comboTimer) clearTimeout(comboTimer);
      if (comboInterval) clearInterval(comboInterval);
      if (skillCooldownInterval) clearInterval(skillCooldownInterval);
      if (skillEffectInterval) clearInterval(skillEffectInterval);
      if (charmRefreshInterval) clearInterval(charmRefreshInterval);
    });

    return {
      currentTab, myData, isShaking, floatingTexts, toastVisible, toastMsg, toastType, closeDialog, paytableOpen,
      combo, comboMultiplier, comboClass, comboPercent, goldenCow, catchGoldenCow,
      currentLevelIndex, currentLevel, currentMap, nextLevelName, levelPercent,
      shopUpgrades, getLevel, getShopCost, canAffordShop, isMaxed, buyShopUpgrade,
      goldUpgrades, getGoldLevel, getGoldCost, canAffordGold, isGoldMaxed, buyGoldUpgrade, getGoldUpgradeDesc,
      clickPower, autoClickPower, handleClick, rankingList, getRankClass, getPlayerPic,
      SYMBOLS, TIER_INFO, symbolsByTier, PATTERN_INFO,
      slotGrid, slotBet, slotSpinning, slotResult, slotWinCells, slotMatches, slotBonus, showPatternOverlay,
      spinSlot, adjustBet, setBet, clampBet, isSlotUnlocked, maxBetAmount, handleSlotTabClick,
      particles, screenShake, bigWinEffect, jackpotEffect, lightningEffect, fireworksEffect, winPulse,
      getWinClass, getWinLabel, playButton, formatNumber,
      currentEvent, eventVisible, eventAnimating, lockedCells, wildCells, isDoubleChance, isHighRiskBet, SLOT_EVENTS,
      currentWeekLabel, showSettlement, settlementRanking, towerAnimated, mySettlementRank,
      settlementProcessing, pendingConquest, confirmSettlement,
      typeEx, CHARACTER_NAMES, conqueredTypes, isAllCleared, conquestVisible, newCharacterType, conqueredCharName,
      historyList, conquestBuffs, lastConqueredType,
      // 캐릭터 특성
      CHARACTER_TRAITS, currentCharTrait, activeSkillCooldown, activeSkillEffect, useActiveSkill,
      // Charm
      CHARMS, showCharmShop, activeCharms, activeCharmsCount, charmShopItems, charmShopRefreshTime,
      buyCharm, refreshCharmShop, totalCharmBonus,
      // 프레스티지
      showPrestigeModal, canPrestige, doPrestige, prestigeProcessing,
    };
  }
};
</script>


<style scoped>
/* 기본 애니메이션 */
@keyframes shake { 0%,100%{transform:translate(0,0)} 25%{transform:translate(-5px,-5px)} 50%{transform:translate(5px,5px)} 75%{transform:translate(-5px,5px)} }
@keyframes floatUp { 0%{opacity:1;transform:translateY(0) scale(1)} 50%{opacity:1;transform:translateY(-25px) scale(1.1)} 100%{opacity:0;transform:translateY(-50px) scale(0.8)} }
@keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
@keyframes glow { 0%,100%{filter:drop-shadow(0 0 8px gold)} 50%{filter:drop-shadow(0 0 25px gold) drop-shadow(0 0 40px orange)} }
@keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }
@keyframes winGlow { 0%,100%{box-shadow:0 0 10px rgba(34,197,94,0.5)} 50%{box-shadow:0 0 25px rgba(34,197,94,0.8)} }
@keyframes rainbowGlow { 0%{box-shadow:0 0 20px #ff0000} 33%{box-shadow:0 0 20px #00ff00} 66%{box-shadow:0 0 20px #0088ff} 100%{box-shadow:0 0 20px #ff0000} }
@keyframes neonPulse { 0%,100%{box-shadow:0 0 5px #00ff88,0 0 10px #00ff88,0 0 20px #00ff88} 50%{box-shadow:0 0 10px #00ff88,0 0 20px #00ff88,0 0 40px #00ff88} }
@keyframes winPopIn { 0%{transform:translate(-50%,-50%) scale(0);opacity:0} 50%{transform:translate(-50%,-50%) scale(1.2)} 100%{transform:translate(-50%,-50%) scale(1);opacity:1} }
@keyframes charWave { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
@keyframes eventSlideIn { 0%{transform:translateY(-100px) scale(0.5);opacity:0} 50%{transform:translateY(10px) scale(1.1)} 100%{transform:translateY(0) scale(1);opacity:1} }
@keyframes eventPulse { 0%,100%{transform:scale(1);box-shadow:0 0 20px var(--event-color)} 50%{transform:scale(1.02);box-shadow:0 0 40px var(--event-color)} }
@keyframes lockedGlow { 0%,100%{box-shadow:0 0 10px gold,inset 0 0 10px rgba(255,215,0,0.3)} 50%{box-shadow:0 0 25px gold,inset 0 0 20px rgba(255,215,0,0.5)} }
@keyframes wildGlow { 0%,100%{box-shadow:0 0 10px #a855f7,inset 0 0 10px rgba(168,85,247,0.3)} 50%{box-shadow:0 0 25px #a855f7,inset 0 0 20px rgba(168,85,247,0.5)} }
@keyframes skillPulse { 0%,100%{filter:drop-shadow(0 0 10px #a855f7)} 50%{filter:drop-shadow(0 0 25px #a855f7) drop-shadow(0 0 35px #6366f1)} }

.screen-shake { animation: shake 0.5s ease; }

/* 토스트 */
.toast { position:fixed;top:15%;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.9);color:#fff;padding:14px 28px;border-radius:14px;font-weight:600;z-index:99999;box-shadow:0 8px 32px rgba(0,0,0,0.3) }
.toast p { margin:0;font-size:15px }
.toast.jackpot { background:linear-gradient(135deg,#ffd700,#ff8c00);color:#000;animation:pulse 0.3s ease infinite }
.toast.bigwin { background:linear-gradient(135deg,#ff6b6b,#ee5a24);animation:pulse 0.4s ease infinite }
.toast.nice { background:linear-gradient(135deg,#a855f7,#6366f1) }
.toast.bonus { background:linear-gradient(135deg,#f59e0b,#ef4444) }
.toast.win { background:linear-gradient(135deg,#22c55e,#16a34a) }
.toast.small { background:rgba(34,197,94,0.9);font-size:13px;padding:10px 20px }
.toast.coin { background:linear-gradient(135deg,#fbbf24,#f59e0b);color:#000 }
.toast.event { background:linear-gradient(135deg,#8b5cf6,#6366f1) }
.toast.bad { background:linear-gradient(135deg,#ef4444,#dc2626) }
.toast.unlock,.toast.upgrade { background:linear-gradient(135deg,#06b6d4,#0891b2) }
.toast.warning { background:rgba(100,100,100,0.95) }
.toast.skill { background:linear-gradient(135deg,#a855f7,#ec4899) }
.toast.charm { background:linear-gradient(135deg,#8b5cf6,#3b82f6) }
.toast.prestige { background:linear-gradient(135deg,#ffd700,#ff6b6b);color:#000 }

.toast-enter-active { animation: floatUp 2s ease-out reverse }
.toast-leave-active { animation: floatUp 0.3s ease-out }

/* 기본 레이아웃 */
.dialog { position:absolute;inset:0;background:var(--color-surface);display:flex;justify-content:center;align-items:center;z-index:99 }
.dialog>.cls { position:absolute;inset:0;z-index:-1 }
.dialog>.paper { width:94%;max-width:420px;height:94%;background:var(--color-background);border-radius:20px;padding:12px;display:flex;flex-direction:column;gap:10px;overflow:hidden;box-shadow:0 10px 40px rgba(0,0,0,0.15) }

/* 탭 메뉴 */
.tab-menu { display:flex;gap:4px;background:var(--color-background-light);padding:4px;border-radius:12px }
.tab-menu button { flex:1;padding:8px 4px;border:none;background:transparent;border-radius:8px;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:2px;transition:all 0.2s;position:relative }
.tab-menu button.active { background:var(--color-brand);color:var(--color-text-black) }
.tab-menu button.locked { opacity:0.6 }
.tab-icon { font-size:18px }
.tab-label { font-size:10px;font-weight:500 }
.coin-badge,.charm-badge { position:absolute;top:2px;right:6px;background:#f59e0b;color:#fff;font-size:9px;padding:1px 4px;border-radius:6px;font-weight:bold }
.charm-badge { background:#8b5cf6 }

.tab-content { flex:1;display:flex;flex-direction:column;overflow:hidden }

/* 게임 탭 */
.game-tab { overflow-y:auto;gap:8px;position:relative; }

/* 프레스티지 배지 */
.prestige-badge-wrap { position:absolute;top:12px;right:12px;display:flex;align-items:center;gap:4px;background:linear-gradient(135deg,#ffd700,#ff8c00);padding:4px 8px;border-radius:12px;cursor:pointer;z-index:10 }
.prestige-badge-icon { font-size:14px }
.prestige-badge-level { font-size:11px;font-weight:bold;color:#333 }

/* 캐릭터 특성 표시 */
.current-trait { background:linear-gradient(135deg,rgba(139,92,246,0.1),rgba(168,85,247,0.1));border:1px solid rgba(139,92,246,0.3);border-radius:12px;padding:10px;margin-bottom:8px }
.trait-header { display:flex;align-items:center;gap:6px;margin-bottom:8px }
.trait-char-icon { font-size:18px }
.trait-char-name { font-size:13px;font-weight:bold;color:#8b5cf6 }
.trait-passive,.trait-active { display:flex;align-items:center;gap:8px;padding:6px 8px;background:rgba(255,255,255,0.5);border-radius:8px;margin-top:4px }
.trait-active { cursor:pointer;border:1px solid transparent }
.trait-active:hover { border-color:#8b5cf6 }
.trait-label { font-size:9px;background:#8b5cf6;color:#fff;padding:2px 6px;border-radius:4px }
.trait-desc { flex:1;font-size:11px;color:var(--color-text) }
.trait-cooldown { font-size:10px;color:#ef4444;font-weight:bold }
.trait-ready { font-size:10px;color:#22c55e;font-weight:bold;animation:pulse 1s ease infinite }

/* 활성 스킬 효과 */
.active-skill-effect { position:absolute;top:130px;left:10px;display:flex;align-items:center;gap:6px;background:linear-gradient(135deg,#a855f7,#6366f1);padding:6px 10px;border-radius:10px;z-index:20;animation:pulse 1s ease infinite }
.skill-icon { font-size:16px }
.skill-name { font-size:11px;color:#fff;font-weight:bold }
.skill-timer { font-size:10px;color:#ffd700 }

.playground { position:relative;width:100%;aspect-ratio:1/1;flex-shrink:0;background-size:cover;background-position:center;border-radius:16px;overflow:hidden;display:flex;align-items:center;justify-content:center }

.resource-display { position:absolute;top:10px;left:10px;display:flex;flex-direction:column;gap:4px }
.resource-item { display:flex;align-items:center;gap:4px;background:rgba(0,0,0,0.7);backdrop-filter:blur(4px);padding:5px 10px;border-radius:14px }
.resource-item.score { border:1px solid rgba(255,215,0,0.3) }
.resource-item.coins { border:1px solid rgba(255,165,0,0.3) }
.resource-icon { font-size:14px }
.resource-value { font-size:14px;font-weight:bold;color:#fff }

.stats-display { position:absolute;top:10px;right:10px;display:flex;flex-direction:column;gap:4px }
.stats-display span { background:rgba(0,0,0,0.7);color:#fff;padding:4px 8px;border-radius:10px;font-size:10px }

.level-badge { position:absolute;top:80px;left:10px;background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:5px 10px;border-radius:10px;display:flex;flex-direction:column;align-items:center;gap:1px }
.level-num { font-size:9px;opacity:0.9 }
.level-name { font-size:12px;font-weight:bold }

/* 콤보 */
.combo-display { position:absolute;top:70px;right:10px;background:rgba(0,0,0,0.8);padding:6px 10px;border-radius:10px;text-align:center;min-width:70px }
.combo-count { display:block;font-size:12px;font-weight:bold;color:#fff }
.combo-multi { font-size:10px;color:#aaa }
.combo-bar { height:3px;background:rgba(255,255,255,0.2);border-radius:2px;margin-top:4px;overflow:hidden }
.combo-fill { height:100%;background:var(--color-brand);transition:width 0.05s linear }
.combo-display.uncommon .combo-count { color:#4fc3f7 }
.combo-display.rare .combo-count { color:#ab47bc }
.combo-display.epic .combo-count { color:#ff9800 }
.combo-display.legendary .combo-count { color:#ffd700;text-shadow:0 0 10px gold }
.combo-enter-active,.combo-leave-active { transition:all 0.2s ease }
.combo-enter-from,.combo-leave-to { opacity:0;transform:scale(0.8) }

/* 황금소 */
.golden-cow { position:absolute;cursor:pointer;z-index:30 }
.golden-cow:hover { transform:scale(1.1) }
.golden-cow img { width:60px;animation:bounce 0.6s ease infinite, glow 1s ease infinite }
.golden-timer { position:absolute;bottom:-6px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.8);color:gold;padding:2px 6px;border-radius:6px;font-size:10px;font-weight:bold }
.golden-reward { position:absolute;top:-10px;left:50%;transform:translateX(-50%);background:linear-gradient(135deg,gold,#ffa500);color:#333;padding:2px 6px;border-radius:6px;font-size:9px;font-weight:bold;white-space:nowrap }
.golden-enter-active { animation: bounce 0.5s ease }
.golden-leave-active { animation: floatUp 0.3s ease-out }

.character { width:42%;max-width:170px;cursor:pointer;transition:transform 0.1s, filter 0.3s;user-select:none;-webkit-user-drag:none;filter:drop-shadow(0 8px 20px rgba(0,0,0,0.3)) }
.character:hover { transform:scale(1.02) }
.character:active { transform:scale(0.92) }
.character.shake { animation:shake 0.1s ease }
.character.skill-active { animation:skillPulse 0.5s ease infinite }

.floating-text { position:absolute;font-size:16px;font-weight:bold;color:#fff;text-shadow:2px 2px 4px rgba(0,0,0,0.5);pointer-events:none;animation:floatUp 0.8s ease-out forwards;z-index:20 }
.floating-text.crit { font-size:22px;color:#ffeb3b;text-shadow:0 0 10px rgba(255,235,59,0.8) }
.floating-text.auto { font-size:11px;color:#4fc3f7 }
.floating-text.coin { font-size:18px;color:#ffd700 }
.floating-text.skill { color:#a855f7;text-shadow:0 0 10px rgba(168,85,247,0.8) }

.level-progress { position:absolute;bottom:10px;left:10px;right:10px;background:rgba(0,0,0,0.7);backdrop-filter:blur(4px);padding:8px 12px;border-radius:10px }
.progress-header { display:flex;justify-content:space-between;margin-bottom:4px;font-size:11px;color:#fff }
.progress-bar { height:8px;background:rgba(255,255,255,0.2);border-radius:4px;overflow:hidden }
.progress-fill { height:100%;background:linear-gradient(90deg,var(--color-brand),#ffd700);transition:width 0.3s;border-radius:4px }

/* 정복 현황 */
.conquest-status { padding:10px;background:linear-gradient(135deg,rgba(255,215,0,0.08),rgba(255,165,0,0.08));border:1px solid rgba(255,215,0,0.25);border-radius:12px;margin-bottom:8px }
.conquest-status .conquest-header { display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;font-size:12px;font-weight:600 }
.conquest-status .conquest-count { color:#f59e0b }
.conquest-grid { display:flex;gap:6px;justify-content:center;flex-wrap:wrap }
.conquest-slot { position:relative;width:36px;height:36px;border-radius:8px;background:var(--color-background-light);border:2px solid var(--color-border);opacity:0.4;display:flex;align-items:center;justify-content:center }
.conquest-slot img { width:28px;height:28px;border-radius:4px }
.conquest-slot.conquered { opacity:1;border-color:#22c55e;background:rgba(34,197,94,0.1) }
.conquest-slot.conquered .check { position:absolute;bottom:-4px;right:-4px;background:#22c55e;color:#fff;font-size:10px;width:14px;height:14px;border-radius:50%;display:flex;align-items:center;justify-content:center }
.conquest-slot.current { opacity:1;border-color:#f59e0b;box-shadow:0 0 8px rgba(245,158,11,0.4) }
.conquest-slot .current-badge { position:absolute;bottom:-6px;left:50%;transform:translateX(-50%);background:#f59e0b;color:#fff;font-size:8px;padding:1px 4px;border-radius:4px;font-weight:bold }
.all-clear-badge { text-align:center;margin-top:8px;padding:6px;background:linear-gradient(135deg,#ffd700,#ff8c00);border-radius:8px;font-size:11px;font-weight:bold;color:#333;animation:pulse 2s ease infinite;cursor:pointer }

/* 랭킹 */
.ranking-section { flex:1;display:flex;flex-direction:column;min-height:150px }
.ranking-section .ranking-header { display:flex;justify-content:space-between;align-items:center;padding:4px 0 8px }
.ranking-section .ranking-header h3 { margin:0;font-size:14px }
.week-indicator { font-size:10px;color:var(--color-text-muted);background:var(--color-background-light);padding:2px 8px;border-radius:10px }
.ranking-list { list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:6px;flex:1;overflow-y:auto }
.ranking-list li { display:flex;align-items:center;gap:8px;background:var(--color-background-light);padding:8px 12px;border-radius:10px }
.ranking-list li.mine { background:linear-gradient(135deg,rgba(102,126,234,0.15),rgba(118,75,162,0.15));border:1px solid rgba(102,126,234,0.3) }
.rank-num { width:26px;height:26px;display:flex;align-items:center;justify-content:center;background:var(--color-background);border-radius:50%;font-size:11px;font-weight:bold;flex-shrink:0 }
.rank-num.gold { background:linear-gradient(135deg,#ffd700,#ffb300);color:#333 }
.rank-num.silver { background:linear-gradient(135deg,#c0c0c0,#a0a0a0);color:#333 }
.rank-num.bronze { background:linear-gradient(135deg,#cd7f32,#b5651d);color:#fff }
.rank-pic { width:30px;height:30px;border-radius:6px;flex-shrink:0 }
.rank-name { flex:1;font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap }
.rank-score { font-size:11px;font-weight:600;color:#f59e0b }
.rank-conquered { font-size:9px;color:#22c55e }
.rank-prestige { font-size:9px;color:#ffd700;font-weight:bold }

/* 슬롯 탭 */
.slot-tab { padding:0;overflow:hidden;background:var(--color-background) }
.slot-locked { flex:1;display:flex;align-items:center;justify-content:center;background:var(--color-background-light);border-radius:16px;margin:12px }
.locked-content { text-align:center;padding:40px 20px }
.locked-icon { font-size:56px;display:block;margin-bottom:16px;opacity:0.5 }
.locked-content h3 { margin:0 0 8px;color:var(--color-text);font-size:18px }
.locked-content p { margin:0 0 20px;color:var(--color-text-muted);font-size:13px }
.unlock-btn { padding:12px 24px;background:var(--color-brand);border:none;border-radius:12px;color:var(--color-text-black);font-size:14px;font-weight:bold;cursor:pointer }

.slot-game { display:flex;flex-direction:column;gap:10px;padding:12px;height:100%;overflow-y:auto }

.slot-header { display:flex;justify-content:center;gap:10px;flex-wrap:wrap }
.balance-display { background:var(--color-background-light);border:1px solid var(--color-border);padding:8px 14px;border-radius:10px;display:flex;align-items:center;gap:6px }
.balance-label { font-size:10px;color:var(--color-text-muted) }
.balance-value { font-size:15px;font-weight:bold;color:#f59e0b }
.bet-limit-display { background:var(--color-background-light);border:1px solid var(--color-border);padding:8px 12px;border-radius:10px;display:flex;align-items:center;gap:6px }
.limit-label { font-size:10px;color:var(--color-text-muted) }
.limit-value { font-size:13px;font-weight:bold;color:var(--color-brand) }
.limit-level { font-size:8px;color:var(--color-brand);background:rgba(var(--color-brand-rgb),0.1);padding:2px 6px;border-radius:4px }

/* Charm 상점 토글 */
.charm-shop-toggle { display:flex;align-items:center;justify-content:center;gap:8px;padding:10px;background:linear-gradient(135deg,rgba(139,92,246,0.1),rgba(168,85,247,0.1));border:1px solid rgba(139,92,246,0.3);border-radius:10px;cursor:pointer }
.charm-toggle-icon { font-size:18px }
.charm-toggle-text { font-size:13px;font-weight:600;color:#8b5cf6 }
.charm-toggle-count { font-size:10px;color:#22c55e;background:rgba(34,197,94,0.1);padding:2px 6px;border-radius:4px }
.charm-toggle-arrow { font-size:10px;color:#8b5cf6 }

/* Charm 상점 패널 */
.charm-shop-panel { background:var(--color-background-light);border:1px solid var(--color-border);border-radius:12px;padding:12px;margin-top:-5px }
.charm-panel-enter-active,.charm-panel-leave-active { transition:all 0.3s ease }
.charm-panel-enter-from,.charm-panel-leave-to { opacity:0;max-height:0;padding:0;margin:0 }

.section-title { font-size:12px;font-weight:600;color:var(--color-text);margin-bottom:8px;display:flex;justify-content:space-between;align-items:center }
.refresh-timer { font-size:10px;color:#f59e0b }

.active-charms-section { margin-bottom:12px }
.active-charms-list { display:flex;gap:6px;flex-wrap:wrap }
.active-charm-item { display:flex;align-items:center;gap:4px;padding:4px 8px;border-radius:6px;font-size:10px }
.active-charm-item.common { background:rgba(156,163,175,0.2);border:1px solid rgba(156,163,175,0.4) }
.active-charm-item.rare { background:rgba(59,130,246,0.2);border:1px solid rgba(59,130,246,0.4) }
.active-charm-item.epic { background:rgba(168,85,247,0.2);border:1px solid rgba(168,85,247,0.4) }
.active-charm-item.legendary { background:rgba(245,158,11,0.2);border:1px solid rgba(245,158,11,0.4) }
.charm-icon { font-size:14px }
.charm-name { font-size:10px }

.charm-shop-slots { display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:10px }
.charm-shop-item { background:var(--color-background);border:2px solid var(--color-border);border-radius:10px;padding:10px 6px;text-align:center;cursor:pointer;transition:all 0.2s }
.charm-shop-item.common { border-color:#9ca3af }
.charm-shop-item.rare { border-color:#3b82f6 }
.charm-shop-item.epic { border-color:#a855f7 }
.charm-shop-item.legendary { border-color:#f59e0b;background:linear-gradient(135deg,#fffbeb,#fff) }
.charm-shop-item.sold { opacity:0.5;cursor:default }
.charm-shop-item.affordable:not(.sold):hover { transform:translateY(-2px);box-shadow:0 4px 12px rgba(0,0,0,0.1) }
.charm-item-icon { font-size:24px;margin-bottom:4px }
.charm-item-name { font-size:10px;font-weight:600;margin-bottom:2px }
.charm-item-desc { font-size:8px;color:var(--color-text-muted);margin-bottom:6px;min-height:24px }
.charm-item-price { display:flex;align-items:center;justify-content:center;gap:2px;font-size:11px;font-weight:bold;color:#f59e0b }
.charm-item-sold { font-size:10px;color:#22c55e }
.refresh-shop-btn { width:100%;padding:8px;background:rgba(139,92,246,0.1);border:1px solid rgba(139,92,246,0.3);border-radius:8px;color:#8b5cf6;font-size:11px;cursor:pointer }
.refresh-shop-btn:disabled { opacity:0.5;cursor:default }

/* 활성 이벤트 표시 */
.active-event { display:flex;align-items:center;justify-content:center;gap:8px;padding:8px 16px;border-radius:10px;font-size:13px;font-weight:600 }
.active-event.good { background:linear-gradient(135deg,rgba(34,197,94,0.2),rgba(34,197,94,0.1));border:1px solid rgba(34,197,94,0.4);color:#22c55e }
.active-event.bad { background:linear-gradient(135deg,rgba(239,68,68,0.2),rgba(239,68,68,0.1));border:1px solid rgba(239,68,68,0.4);color:#ef4444 }
.active-event-icon { font-size:18px }
.active-event-spin { background:var(--color-background);color:#333;padding:2px 6px;border-radius:4px;font-size:10px }
.event-indicator-enter-active,.event-indicator-leave-active { transition:all 0.3s ease }
.event-indicator-enter-from,.event-indicator-leave-to { opacity:0;transform:translateY(-10px) }

/* 슬롯 머신 */
.slot-machine { display:flex;justify-content:center }
.slot-machine.machine-glow .slot-frame { animation:neonPulse 1s ease infinite }
.slot-frame { position:relative;background:var(--color-background-light);padding:12px;border-radius:16px;border:3px solid var(--color-border);box-shadow:0 4px 16px rgba(0,0,0,0.06) }
.neon-border { position:absolute;inset:-3px;border-radius:18px;opacity:0;transition:opacity 0.3s ease;pointer-events:none }
.neon-border.active { opacity:1;background:linear-gradient(45deg,#ff0000,#ff8800,#ffff00,#00ff00,#0088ff,#8800ff,#ff0000);background-size:400% 400%;animation:rainbowMove 2s linear infinite;-webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);-webkit-mask-composite:xor;mask-composite:exclude;padding:3px }
@keyframes rainbowMove { 0%{background-position:0% 50%} 100%{background-position:400% 50%} }

.slot-grid { display:grid;grid-template-columns:repeat(5,1fr);grid-template-rows:repeat(3,1fr);gap:5px }
.slot-cell { width:48px;height:48px;display:flex;align-items:center;justify-content:center;background:var(--color-background);border-radius:8px;border:2px solid var(--color-border);transition:all 0.2s;position:relative;overflow:hidden }
.slot-cell .cell-inner { display:flex;align-items:center;justify-content:center;width:100%;height:100%;position:relative }
.slot-cell.winner { border-color:#22c55e;background:linear-gradient(135deg,#f0fdf4,#dcfce7);animation:winGlow 0.5s ease infinite;transform:scale(1.05);z-index:2 }
.slot-cell.winner.bonus { border-color:#f59e0b;background:linear-gradient(135deg,#fffbeb,#fef3c7) }
.slot-cell.rainbow-glow { animation:rainbowGlow 0.5s linear infinite !important }
.slot-cell.locked-cell { animation:lockedGlow 1s ease infinite;border-color:gold }
.slot-cell.wild-cell { animation:wildGlow 1s ease infinite;border-color:#a855f7 }
.locked-indicator,.wild-indicator { position:absolute;top:2px;right:2px;font-size:10px;z-index:5 }

.slot-symbol { width:32px;height:32px;object-fit:contain;border-radius:5px;transition:transform 0.2s }
.slot-cell.spinning .slot-symbol { animation:slotSpinImg 0.06s linear infinite }
@keyframes slotSpinImg { 0%{transform:translateY(-80%) scale(0.85);opacity:0.3} 50%{transform:translateY(0) scale(1);opacity:1} 100%{transform:translateY(80%) scale(0.85);opacity:0.3} }
.slot-cell.winner .slot-symbol { animation:winSymbol 0.3s ease infinite alternate }
@keyframes winSymbol { 0%{transform:scale(1)} 100%{transform:scale(1.15) rotate(5deg)} }

.cell-particles { position:absolute;inset:0;pointer-events:none }
.cell-spark { position:absolute;top:50%;left:50%;font-size:10px;animation:sparkRotate 1.5s ease infinite }
.cell-spark::before { content:'✨' }
@keyframes sparkRotate { 0%{transform:rotate(0deg) translateX(20px) scale(0);opacity:0} 50%{transform:rotate(180deg) translateX(25px) scale(1);opacity:1} 100%{transform:rotate(360deg) translateX(20px) scale(0);opacity:0} }

/* 결과 표시 */
.win-popup-enter-active { animation:winPopIn 0.5s cubic-bezier(0.68,-0.55,0.265,1.55) }
.win-popup-leave-active { animation:winPopIn 0.3s ease reverse }
.slot-result { position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:10;pointer-events:none }
.win-display { background:var(--color-background);border:3px solid #22c55e;padding:16px 24px;border-radius:16px;box-shadow:0 10px 40px rgba(34,197,94,0.3);text-align:center;min-width:180px;position:relative;overflow:hidden }
.win-glow { position:absolute;inset:0;background:radial-gradient(circle at center,rgba(34,197,94,0.2) 0%,transparent 70%);animation:glowPulse 1s ease infinite }
@keyframes glowPulse { 0%,100%{opacity:0.5;transform:scale(1)} 50%{opacity:1;transform:scale(1.5)} }
.win-label { display:flex;justify-content:center;font-size:20px;color:#22c55e;font-weight:bold;margin-bottom:4px;text-shadow:0 0 10px rgba(34,197,94,0.5) }
.win-char { display:inline-block;animation:charWave 0.6s ease infinite;animation-delay:calc(var(--char-i) * 0.08s) }
.win-amount { display:block;font-size:28px;color:#f59e0b;font-weight:bold;text-shadow:0 2px 10px rgba(245,158,11,0.4) }
.win-amount .plus { color:#22c55e }
.win-multiplier { display:inline-block;margin-top:4px;padding:4px 10px;background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;border-radius:16px;font-size:12px;font-weight:bold }
.event-effect-notice,.charm-effect-notice { margin-top:6px;padding:4px 10px;background:rgba(139,92,246,0.1);border:1px solid rgba(139,92,246,0.3);border-radius:6px;font-size:10px;color:#8b5cf6;font-weight:500 }
.slot-result.jackpot-win .win-display { background:linear-gradient(135deg,#fffbeb,#fef3c7);border-color:#ffd700;box-shadow:0 0 40px rgba(255,215,0,0.6) }
.slot-result.jackpot-win .win-label { color:#ffd700;font-size:24px }
.slot-result.big-win .win-display { background:linear-gradient(135deg,#fef2f2,#fee2e2);border-color:#ef4444;box-shadow:0 0 30px rgba(239,68,68,0.5) }
.slot-result.big-win .win-label { color:#ef4444;font-size:22px }
.slot-result.nice-win .win-display { border-color:#a855f7;box-shadow:0 0 25px rgba(168,85,247,0.4) }
.slot-result.nice-win .win-label { color:#a855f7 }

.win-details { margin-top:8px;display:flex;flex-direction:column;gap:2px }
.win-line { font-size:10px;color:var(--color-text-muted);display:flex;align-items:center;justify-content:center;gap:4px }
.win-line.rare { color:#3b82f6 }
.win-line.epic { color:#a855f7 }
.win-line.legendary { color:#f59e0b;font-weight:600 }
.win-symbol { width:16px;height:16px;border-radius:3px }
.win-more { font-size:9px;color:var(--color-text-muted);margin-top:2px }
.bonus-notice { margin-top:8px;padding:6px 10px;background:linear-gradient(135deg,#fef3c7,#fde68a);border:2px solid #fcd34d;border-radius:6px;font-size:10px;color:#b45309;font-weight:600;display:flex;align-items:center;justify-content:center;gap:4px }
.bonus-fire { animation:bounce 0.5s ease infinite }

/* 배팅 컨트롤 */
.bet-controls { display:flex;flex-direction:column;gap:10px;background:var(--color-background-light);padding:12px;border-radius:12px;border:1px solid var(--color-border) }
.bet-section { text-align:center }
.bet-label { display:block;font-size:11px;color:var(--color-text-muted);margin-bottom:6px;font-weight:500 }
.bet-input-wrap { display:flex;align-items:center;justify-content:center;gap:6px }
.bet-btn { padding:6px 10px;background:var(--color-background);border:1px solid var(--color-border);border-radius:6px;color:var(--color-text);font-size:10px;font-weight:500;cursor:pointer }
.bet-btn:hover { background:var(--color-background-light);border-color:var(--color-brand);color:var(--color-brand) }
.bet-input { width:90px;padding:8px 6px;text-align:center;background:var(--color-background);border:2px solid var(--color-brand);border-radius:8px;color:var(--color-text);font-size:15px;font-weight:bold }
.bet-input:focus { outline:none;box-shadow:0 0 0 3px rgba(var(--color-brand-rgb),0.15) }
.bet-presets { display:flex;justify-content:center;gap:4px;flex-wrap:wrap;margin-top:6px }
.bet-presets button { padding:4px 10px;background:var(--color-background);border:1px solid var(--color-border);border-radius:6px;color:var(--color-text-muted);font-size:10px;font-weight:500;cursor:pointer }
.bet-presets button:hover:not(:disabled) { background:var(--color-brand);color:var(--color-text-black);border-color:var(--color-brand) }
.event-hint { margin-top:8px;padding:6px 10px;background:linear-gradient(135deg,rgba(139,92,246,0.1),rgba(168,85,247,0.1));border:1px solid rgba(139,92,246,0.3);border-radius:6px;font-size:10px;color:#8b5cf6;font-weight:500;animation:pulse 2s ease infinite }
.charm-hint { margin-top:4px;font-size:10px;color:#8b5cf6 }

.spin-btn { padding:12px;background:linear-gradient(135deg,var(--color-brand),#fbbf24);border:none;border-radius:10px;color:var(--color-text-black);font-size:15px;font-weight:bold;cursor:pointer;box-shadow:0 4px 15px rgba(0,0,0,0.15);display:flex;align-items:center;justify-content:center;gap:6px }
.spin-btn:hover:not(:disabled) { transform:translateY(-2px);box-shadow:0 8px 25px rgba(0,0,0,0.2) }
.spin-btn:disabled { background:var(--color-background-light);color:var(--color-text-muted);box-shadow:none }
.spin-text { font-size:16px }
.spin-cost { font-size:11px;opacity:0.8 }
.spin-anim { display:inline-block;animation:spinAnim 0.12s linear infinite;font-size:22px }
@keyframes spinAnim { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }

/* 배당표 */
.paytable { background:var(--color-background-light);border-radius:12px;border:1px solid var(--color-border);overflow:hidden }
.paytable-header { display:flex;justify-content:space-between;align-items:center;padding:10px 12px;cursor:pointer;font-size:12px;font-weight:600;color:var(--color-text) }
.toggle-icon { font-size:10px;color:var(--color-text-muted) }
.paytable-body { padding:0 12px 12px }

.tier-section,.event-section,.pattern-section { margin-bottom:12px }
.tier-title,.event-title,.pattern-title { font-size:10px;color:var(--color-text-muted);margin-bottom:6px;font-weight:500 }
.tier-grid { display:grid;grid-template-columns:repeat(2,1fr);gap:6px }
.tier-card { background:var(--color-background);border:1px solid var(--color-border);border-radius:8px;padding:8px;border-left:3px solid }
.tier-card.common { border-left-color:#9ca3af }
.tier-card.rare { border-left-color:#3b82f6 }
.tier-card.epic { border-left-color:#a855f7 }
.tier-card.legendary { border-left-color:#f59e0b;background:linear-gradient(135deg,#fffbeb,#fff) }
.tier-header { display:flex;justify-content:space-between;align-items:center;margin-bottom:4px }
.tier-name { font-size:10px;font-weight:600 }
.tier-card.common .tier-name { color:#6b7280 }
.tier-card.rare .tier-name { color:#3b82f6 }
.tier-card.epic .tier-name { color:#a855f7 }
.tier-card.legendary .tier-name { color:#d97706 }
.tier-mult { font-size:11px;font-weight:bold;color:#22c55e }
.tier-symbols { display:flex;gap:3px }
.tier-img { width:20px;height:20px;object-fit:contain;border-radius:3px;background:var(--color-background-light);padding:2px }

.event-list { display:grid;grid-template-columns:repeat(2,1fr);gap:4px }
.event-item { display:flex;flex-direction:column;padding:6px;border-radius:6px;font-size:9px;text-align:center }
.event-item.good { background:rgba(34,197,94,0.1);border:1px solid rgba(34,197,94,0.2) }
.event-item.bad { background:rgba(239,68,68,0.1);border:1px solid rgba(239,68,68,0.2) }
.event-item span:first-child { font-weight:600;margin-bottom:1px }
.event-item.good span:first-child { color:#22c55e }
.event-item.bad span:first-child { color:#ef4444 }

.pattern-grid { display:grid;grid-template-columns:repeat(3,1fr);gap:4px }
.pattern-item { background:var(--color-background);border:1px solid var(--color-border);border-radius:6px;padding:6px 4px;text-align:center }
.pattern-visual { display:block;font-size:12px;margin-bottom:1px }
.pattern-name { display:block;font-size:8px;color:var(--color-text-muted);margin-bottom:1px }
.pattern-mult { font-size:10px;font-weight:bold;color:#22c55e }
.pattern-mult.bonus { color:#f59e0b }
.paytable-note { text-align:center;font-size:9px;color:var(--color-text-muted);padding:6px;background:var(--color-background);border-radius:6px;border:1px dashed var(--color-border) }

/* 상점 */
.shop-tab { padding:8px;gap:10px;overflow-y:auto }
.shop-header { display:flex;justify-content:space-between;align-items:center;margin-bottom:8px }
.shop-header h3 { margin:0;font-size:15px }
.shop-header.gold h3 { color:#f59e0b }
.score-badge { background:linear-gradient(135deg,#ffd700,#ffb300);color:#333;padding:5px 10px;border-radius:10px;font-size:12px;font-weight:bold }
.coin-badge-large { background:linear-gradient(135deg,#f59e0b,#ea580c);color:#fff;padding:5px 12px;border-radius:10px;font-size:13px;font-weight:bold }
.gold-shop-desc { text-align:center;font-size:12px;color:var(--color-text-muted);margin:0 0 12px }

.upgrade-list { list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:8px }
.upgrade-list li { display:flex;align-items:center;justify-content:space-between;background:var(--color-background-light);padding:12px;border-radius:12px;border:2px solid transparent }
.upgrade-list li.affordable { border-color:rgba(102,126,234,0.5) }
.upgrade-list li.maxed { opacity:0.5 }
.upgrade-list li.featured { background:linear-gradient(135deg,rgba(156,39,176,0.1),rgba(103,58,183,0.1));border-color:rgba(156,39,176,0.3) }
.upgrade-list.gold li.affordable { border-color:rgba(245,158,11,0.5) }
.upgrade-info { display:flex;align-items:center;gap:10px;flex:1 }
.upgrade-icon { font-size:24px;width:40px;height:40px;display:flex;align-items:center;justify-content:center;background:var(--color-background);border-radius:10px }
.upgrade-text h4 { margin:0;font-size:13px }
.upgrade-text p { margin:2px 0;font-size:10px;color:var(--color-text-muted) }
.upgrade-level { font-size:9px;color:var(--color-brand) }
.upgrade-list button { padding:8px 12px;border:none;background:var(--color-brand);color:var(--color-text-black);border-radius:8px;font-size:11px;font-weight:bold;cursor:pointer;white-space:nowrap }
.upgrade-list button:disabled { background:var(--color-background);color:var(--color-text-muted) }
.upgrade-list.gold button { background:linear-gradient(135deg,#f59e0b,#ea580c);color:#fff }
.upgrade-list.gold button:disabled { background:var(--color-background);color:var(--color-text-muted) }

/* 기록 탭 */
.history-tab { padding:8px;overflow-y:auto }
.history-header h3 { margin:0 0 12px;font-size:15px }
.history-list { display:flex;flex-direction:column;gap:8px }
.history-item { display:flex;align-items:center;gap:10px;padding:12px;background:var(--color-background-light);border-radius:10px }
.history-item.top-rank { background:linear-gradient(135deg,rgba(255,215,0,0.1),rgba(255,165,0,0.1));border:1px solid rgba(255,215,0,0.3) }
.history-week { font-size:11px;font-weight:600;min-width:65px }
.history-rank { font-size:14px;min-width:40px }
.history-score { flex:1;font-size:12px;color:#f59e0b }
.history-char { width:28px;height:28px;border-radius:6px;border:1px solid var(--color-border) }
.history-conquest { font-size:10px;color:#f59e0b;font-weight:bold }
.history-empty { text-align:center;padding:40px;color:var(--color-text-muted) }

/* 이벤트 오버레이 */
.event-overlay { position:fixed;inset:0;display:flex;align-items:center;justify-content:center;z-index:99998;pointer-events:none }
.event-backdrop { position:absolute;inset:0;background:rgba(0,0,0,0.5);backdrop-filter:blur(4px) }
.event-banner { position:relative;display:flex;align-items:center;gap:16px;padding:20px 32px;border-radius:20px;background:var(--color-background);border:4px solid var(--event-color);box-shadow:0 20px 60px rgba(0,0,0,0.3);animation:eventSlideIn 0.5s cubic-bezier(0.68,-0.55,0.265,1.55),eventPulse 1s ease infinite 0.5s }
.event-icon-wrap { width:60px;height:60px;display:flex;align-items:center;justify-content:center;background:var(--event-color);border-radius:50%;box-shadow:0 0 20px var(--event-color) }
.event-icon { font-size:32px }
.event-content { display:flex;flex-direction:column;gap:4px }
.event-name { font-size:24px;font-weight:bold;color:var(--event-color) }
.event-desc { font-size:14px;color:var(--color-text-muted) }
.event-particles { position:absolute;inset:0;pointer-events:none;overflow:hidden;border-radius:20px }
.event-particle { position:absolute;width:8px;height:8px;background:var(--event-color);border-radius:50%;opacity:0;animation:eventParticleFly 1.5s ease-out infinite;animation-delay:calc(var(--p-i) * 0.1s) }
@keyframes eventParticleFly { 0%{opacity:0;transform:translate(0,0) scale(0)} 20%{opacity:1;transform:scale(1)} 100%{opacity:0;transform:translate(calc((var(--p-i) - 4) * 40px),-100px) scale(0.5)} }
.event-banner-enter-active { animation:eventSlideIn 0.5s cubic-bezier(0.68,-0.55,0.265,1.55) }
.event-banner-leave-active { animation:eventSlideIn 0.3s ease reverse }
.event-overlay.good .event-banner { border-color:#22c55e;--event-color:#22c55e }
.event-overlay.bad .event-banner { border-color:#ef4444;--event-color:#ef4444 }

/* 글로벌 이펙트 */
.particles-container { position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:99990 }
.particle { position:absolute;width:12px;height:12px;border-radius:50%;animation:particleFly var(--duration,2s) ease-out forwards;animation-delay:var(--delay,0s) }
.particle.coin { width:14px;height:14px;background:linear-gradient(135deg,#ffd700,#ffb300);border:2px solid #b8860b;box-shadow:0 0 8px rgba(255,215,0,0.6) }
.particle.star { background:#ffd700;clip-path:polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%) }
.particle.sparkle { width:8px;height:8px;background:white;box-shadow:0 0 10px 3px rgba(255,255,255,0.8) }
@keyframes particleFly { 0%{opacity:0;transform:translateY(0) translateX(0) scale(0)} 10%{opacity:1;transform:scale(var(--scale,1))} 100%{opacity:0;transform:translateY(calc(100vh + var(--vy,100px))) translateX(var(--vx,0px)) rotate(calc(var(--rotation,0) + 720deg)) scale(0.3)} }

/* 잭팟/빅윈 오버레이 */
.jackpot-overlay,.bigwin-overlay { position:fixed;inset:0;display:flex;align-items:center;justify-content:center;z-index:99995;pointer-events:none }
.jackpot-overlay { background:radial-gradient(circle at center,rgba(255,215,0,0.3) 0%,rgba(0,0,0,0.7) 100%) }
.jackpot-rays { position:absolute;inset:0;background:conic-gradient(from 0deg,transparent,rgba(255,215,0,0.3),transparent,rgba(255,215,0,0.3),transparent);animation:raysSpin 3s linear infinite }
@keyframes raysSpin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
.jackpot-sparkles { position:absolute;inset:0 }
.sparkle { position:absolute;width:8px;height:8px;background:white;border-radius:50%;box-shadow:0 0 10px 4px rgba(255,255,255,0.8);animation:sparkleFloat 2s ease-in-out infinite;animation-delay:calc(var(--i) * 0.12s) }
.sparkle:nth-child(odd) { left:calc(var(--i) * 5%);top:calc(var(--i) * 5%) }
.sparkle:nth-child(even) { right:calc(var(--i) * 5%);bottom:calc(var(--i) * 5%) }
@keyframes sparkleFloat { 0%,100%{opacity:0;transform:scale(0)} 50%{opacity:1;transform:scale(1.5)} }
.jackpot-text { font-size:48px;font-weight:900;color:#ffd700;text-shadow:0 0 10px rgba(255,215,0,1),0 0 20px rgba(255,215,0,0.8),4px 4px 0 #b8860b;animation:jackpotText 0.2s ease infinite alternate;letter-spacing:4px;display:flex;z-index:1 }
.jackpot-text span { display:inline-block;animation:charWave 0.4s ease infinite;animation-delay:calc(var(--char-i,0) * 0.05s) }
@keyframes jackpotText { 0%{transform:scale(1) rotate(-1deg)} 100%{transform:scale(1.1) rotate(1deg)} }
.jackpot-coins { position:absolute;inset:0;overflow:hidden }
.falling-coin { position:absolute;width:20px;height:20px;background:linear-gradient(135deg,#ffd700,#ffb300);border:3px solid #b8860b;border-radius:50%;top:-30px;animation:coinFall 2s ease-in forwards;animation-delay:calc(var(--coin-i) * 0.06s);box-shadow:0 0 10px rgba(255,215,0,0.5) }
.falling-coin:nth-child(odd) { left:calc(var(--coin-i) * 3%) }
.falling-coin:nth-child(even) { right:calc(var(--coin-i) * 3%) }
@keyframes coinFall { 0%{transform:translateY(0) rotate(0deg);opacity:1} 100%{transform:translateY(120vh) rotate(720deg);opacity:0.5} }
.jackpot-bg-enter-active { animation:jackpotFadeIn 0.3s ease }
.jackpot-bg-leave-active { animation:jackpotFadeOut 0.5s ease }
@keyframes jackpotFadeIn { 0%{opacity:0} 100%{opacity:1} }
@keyframes jackpotFadeOut { 0%{opacity:1} 100%{opacity:0} }

.bigwin-overlay { background:radial-gradient(circle at center,rgba(239,68,68,0.2) 0%,rgba(0,0,0,0.6) 100%) }
.bigwin-burst { position:absolute;width:200px;height:200px;background:radial-gradient(circle,rgba(255,107,107,0.8) 0%,transparent 70%);animation:burstPulse 0.5s ease-out infinite }
@keyframes burstPulse { 0%{transform:scale(0.5);opacity:1} 100%{transform:scale(3);opacity:0} }
.bigwin-text { font-size:40px;font-weight:900;color:#fff;text-shadow:0 0 10px #ef4444,0 0 20px #ef4444,3px 3px 0 #b91c1c;animation:bigwinPulse 0.3s ease infinite alternate;z-index:1 }
@keyframes bigwinPulse { 0%{transform:scale(1)} 100%{transform:scale(1.1)} }
.bigwin-stars { position:absolute;inset:0 }
.burst-star { position:absolute;width:16px;height:16px;background:linear-gradient(135deg,#ffd700,#ff8c00);clip-path:polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%);top:50%;left:50%;animation:starBurst 1s ease-out forwards;animation-delay:calc(var(--star-i) * 0.08s);box-shadow:0 0 10px rgba(255,215,0,0.6) }
@keyframes starBurst { 0%{transform:translate(-50%,-50%) scale(0);opacity:1} 100%{transform:translate(calc(-50% + (var(--star-i) - 6) * 60px),calc(-50% + (var(--star-i) - 6) * 50px)) scale(1.5) rotate(360deg);opacity:0} }
.bigwin-bg-enter-active { animation:jackpotFadeIn 0.3s ease }
.bigwin-bg-leave-active { animation:jackpotFadeOut 0.4s ease }

.lightning-container { position:fixed;inset:0;pointer-events:none;z-index:99993 }
.lightning { position:absolute;top:0;width:100%;height:100%;background:linear-gradient(to bottom,rgba(255,255,255,0.9) 0%,transparent 50%);animation:lightningFlash 0.15s ease-out forwards;animation-delay:calc(var(--l-i) * 0.1s);opacity:0 }
@keyframes lightningFlash { 0%,100%{opacity:0} 10%,30%{opacity:0.8} 20%,40%{opacity:0} 50%{opacity:0.6} 60%{opacity:0} }

.fireworks-container { position:fixed;inset:0;pointer-events:none;z-index:99992;overflow:hidden }
.firework { position:absolute;width:10px;height:10px }
.firework:nth-child(1) { top:20%;left:10% }
.firework:nth-child(2) { top:30%;left:80% }
.firework:nth-child(3) { top:50%;left:20% }
.firework:nth-child(4) { top:40%;left:70% }
.firework:nth-child(5) { top:60%;left:40% }
.firework:nth-child(6) { top:25%;left:50% }
.firework:nth-child(7) { top:70%;left:15% }
.firework:nth-child(8) { top:55%;left:85% }
.firework .spark { position:absolute;width:6px;height:6px;border-radius:50%;background:hsl(calc(var(--fw-i) * 45),100%,60%);box-shadow:0 0 8px 2px hsl(calc(var(--fw-i) * 45),100%,60%);animation:fireworkExplode 1s ease-out forwards;animation-delay:calc(var(--fw-i) * 0.1s + var(--spark-i) * 0.02s) }
@keyframes fireworkExplode { 0%{transform:translate(0,0) scale(0);opacity:1} 50%{opacity:1} 100%{transform:translate(calc(cos(var(--spark-i) * 30deg) * 80px),calc(sin(var(--spark-i) * 30deg) * 80px + 40px)) scale(0);opacity:0} }

/* 주간 결산 모달 */
.settlement-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center;z-index:99999 }
.settlement-modal { background:var(--color-background);border-radius:20px;padding:24px;width:90%;max-width:380px;text-align:center }
.settlement-header .trophy { font-size:48px }
.settlement-header h2 { margin:8px 0 4px;font-size:22px }
.settlement-header .week-label { font-size:12px;color:#888 }

.tower-chart { display:flex;justify-content:center;align-items:flex-end;gap:8px;height:180px;padding:20px 10px;background:#f5f5f5;border-radius:12px;margin:20px 0 }
.tower-item { display:flex;flex-direction:column;align-items:center;width:50px }
.tower-item.mine .tower-name { color:#667eea;font-weight:bold }
.tower-crown { font-size:18px;margin-bottom:4px }
.tower-bar-wrap { width:36px;height:120px;background:#e0e0e0;border-radius:6px 6px 0 0;position:relative;display:flex;flex-direction:column;justify-content:flex-end }
.tower-bar { width:100%;border-radius:6px 6px 0 0;transition:height 2s ease-out }
.tower-bar.rank-1 { background:linear-gradient(to top,#ffd700,#ffec8b) }
.tower-bar.rank-2 { background:linear-gradient(to top,#c0c0c0,#e8e8e8) }
.tower-bar.rank-3 { background:linear-gradient(to top,#cd7f32,#daa06d) }
.tower-bar:not(.rank-1):not(.rank-2):not(.rank-3) { background:linear-gradient(to top,#667eea,#a5b4fc) }
.tower-score { position:absolute;top:4px;left:50%;transform:translateX(-50%);font-size:8px;font-weight:bold;white-space:nowrap }
.tower-char { width:28px;height:28px;margin-top:6px;border-radius:4px }
.tower-name { font-size:9px;margin-top:4px;max-width:50px;overflow:hidden;text-overflow:ellipsis }

.my-result { padding:12px;background:linear-gradient(135deg,#fef3c7,#fde68a);border-radius:10px;margin-bottom:12px;font-size:14px }
.conquest-alert { padding:10px;background:linear-gradient(135deg,#dbeafe,#bfdbfe);border-radius:10px;margin-bottom:12px;font-size:13px;color:#1e40af }
.settlement-btn { width:100%;padding:14px;background:linear-gradient(135deg,#22c55e,#16a34a);border:none;border-radius:12px;color:#fff;font-size:15px;font-weight:bold;cursor:pointer }
.settlement-btn:disabled { background:#ccc }

/* 정복 모달 */
.conquest-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.9);display:flex;align-items:center;justify-content:center;z-index:99999 }
.conquest-modal { background:var(--color-background);border-radius:20px;padding:30px;width:85%;max-width:320px;text-align:center }
.conquest-badge { font-size:60px }
.conquest-modal h2 { margin:10px 0;font-size:24px;color:#f59e0b }
.conquest-modal p { color:#666;margin:0 0 16px }
.conquest-reward { background:linear-gradient(135deg,rgba(168,85,247,0.1),rgba(139,92,246,0.1));border:1px solid rgba(168,85,247,0.3);border-radius:12px;padding:12px;margin-bottom:12px }
.reward-label { display:block;font-size:12px;color:#8b5cf6;margin-bottom:6px }
.reward-detail { display:flex;align-items:center;justify-content:center;gap:8px }
.reward-icon { font-size:20px }
.reward-text { font-size:13px;font-weight:600 }
.conquest-progress { background:#f3f4f6;padding:8px 16px;border-radius:20px;font-size:13px;margin-bottom:16px }
.new-char { margin:20px 0 }
.new-char img { width:80px;display:block;margin:10px auto }
.new-char span { font-size:16px;font-weight:bold }
.new-char-trait { margin-top:8px;padding:6px 12px;background:rgba(168,85,247,0.1);border-radius:8px;font-size:11px;color:#8b5cf6 }
.all-clear { padding:16px;background:linear-gradient(135deg,#fef3c7,#fde68a);border-radius:12px;margin:16px 0;font-weight:bold;color:#b45309 }
.conquest-modal button { width:100%;padding:12px;background:linear-gradient(135deg,#f59e0b,#ea580c);border:none;border-radius:10px;color:#fff;font-size:14px;font-weight:bold;cursor:pointer;margin-top:10px }

/* 프레스티지 모달 */
.prestige-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.9);display:flex;align-items:center;justify-content:center;z-index:99999 }
.prestige-modal { background:#fff;border-radius:20px;padding:24px;width:90%;max-width:340px;text-align:center }
.prestige-header { display:flex;flex-direction:column;align-items:center;margin-bottom:16px }
.prestige-icon { font-size:48px }
.prestige-header h2 { margin:8px 0 4px;font-size:22px }
.prestige-level { font-size:14px;color:#f59e0b;font-weight:bold }
.prestige-info { margin-bottom:20px }
.prestige-info p { font-size:13px;color:#666;margin-bottom:16px }
.prestige-bonuses { display:flex;flex-direction:column;gap:8px }
.bonus-item { display:flex;align-items:center;justify-content:center;gap:8px;padding:10px;background:linear-gradient(135deg,rgba(255,215,0,0.1),rgba(255,165,0,0.1));border:1px solid rgba(255,215,0,0.3);border-radius:10px;font-size:13px }
.bonus-icon { font-size:18px }
.prestige-requirement { margin-top:16px;padding:10px;background:#f3f4f6;border-radius:8px;font-size:12px;color:#888 }
.prestige-buttons { display:flex;gap:10px }
.prestige-btn { flex:1;padding:12px;border:none;border-radius:10px;font-size:14px;font-weight:bold;cursor:pointer }
.prestige-btn.cancel { background:#e5e7eb;color:#374151 }
.prestige-btn.confirm { background:linear-gradient(135deg,#ffd700,#ff8c00);color:#333 }
.prestige-btn:disabled { opacity:0.5;cursor:default }

/* 트랜지션 */
.settlement-enter-active,.settlement-leave-active,.conquest-enter-active,.conquest-leave-active,.prestige-enter-active,.prestige-leave-active { transition:all 0.3s ease }
.settlement-enter-from,.settlement-leave-to,.conquest-enter-from,.conquest-leave-to,.prestige-enter-from,.prestige-leave-to { opacity:0 }
.settlement-enter-from .settlement-modal,.conquest-enter-from .conquest-modal,.prestige-enter-from .prestige-modal { transform:scale(0.8) }
</style>